import { useApiQuery } from '@app/hooks/useApiQuery';
import { collectCursorPages } from '@app/utils/collectCursorPages';
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import OrgModuleLayout from '../OrgModuleLayout';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import KpiCard from '@app/components/KpiCard';
import ShortcutButton from '@app/components/ShortcutButton';
import ListWorkspace, { type ListWorkspaceColumn, type ListWorkspaceProps, type ListWorkspacePaginationMeta } from '@app/components/ListWorkspace';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { ActivityIcon, LayersIcon, PencilIcon, SaveIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useCursorPagination, type CursorPaginationState } from '@app/hooks/useCursorPagination';
import { useApiKeysApi, useIdpConnectionsApi, useInvitationsApi, useMembershipsApi, useRolesApi } from '@app/hooks/useApis';
import { useOrgChangefeedEffect } from '@app/context/org-changefeed';
import type {
  ApiKey,
  ApiKeyCreate,
  ApiKeyCreateResponse,
  ApiKeyUpdate,
  Invitation,
  InvitationCreate,
  Membership,
  MembershipUpdate,
  Permissionschema,
  Role,
  RoleCreate,
  RoleUpdate,
  IdpConnectionschema,
  IdpConnection
} from '@app/types/sdk';
import type { OrgsOrgIdInvitationsGetRequest } from '@reap/openapi-public-v1/dist/esm/apis/InvitationsApi.js';
import type { OrgsOrgIdApiKeysGetRequest } from '@reap/openapi-public-v1/dist/esm/apis/APIKeysApi.js';
import { ApiKeyStatusEnum, ResponseError } from '@app/types/sdk';
import { formatDateTime, formatNumber } from '@app/utils/format';
import { deriveConnectionSource, formatConnectionSourceBadge, formatConnectionStatusBadge } from '@app/utils/idp';
import ConnectionWizard, { type ConnectionWizardResult } from '../sso-directory/ConnectionWizard';
import ConnectionDrawer, { type DrawerTab as ConnectionDrawerTab } from '../sso-directory/ConnectionDrawer';

type TabId = 'members' | 'invitations' | 'roles' | 'api-keys' | 'oidc-connections' | 'saml-connections';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

interface AlertMessage {
  id: string;
  tone: AlertTone;
  title?: string;
  message: string;
}

interface MemberFilters {
  q: string;
  status: 'all' | MembershipStatusValue;
  roleSlug: 'all' | string;
  label: string;
}

interface InvitationFilters {
  status: 'all' | InvitationStatusValue;
  q: string;
}

interface RoleFilters {
  q: string;
  permission: 'all' | PermissionResource;
  usage: 'all' | 'with-members' | 'unused';
}

interface ApiKeyFilters {
  q: string;
  status: 'all' | ApiKeyStatusValue;
}

interface MembershipListState {
  items: Membership[];
  loading: boolean;
  error: string | null;
  backendMissing: boolean;
}

interface InvitationsListState {
  items: Invitation[];
  loading: boolean;
  error: string | null;
  backendMissing: boolean;
}

interface RolesListState {
  items: Role[];
  loading: boolean;
  error: string | null;
  backendMissing: boolean;
}

type ApiKeyStatusValue = (typeof ApiKeyStatusEnum)[keyof typeof ApiKeyStatusEnum];

interface ApiKeysListState {
  items: ApiKey[];
  loading: boolean;
  error: string | null;
  backendMissing: boolean;
}

interface ApiKeyDetailState {
  open: boolean;
  loading: boolean;
  saving: boolean;
  revoking: boolean;
  record: ApiKey | null;
  etag: string | null;
  error: string | null;
  success: string | null;
}

interface ApiKeyCreateState {
  open: boolean;
  saving: boolean;
  error: string | null;
  response: ApiKeyCreateResponse | null;
}

interface MembershipDetailState {
  open: boolean;
  loading: boolean;
  saving: boolean;
  revoking: boolean;
  record: Membership | null;
  etag: string | null;
  error: string | null;
  success: string | null;
}

interface InvitationDetailState {
  open: boolean;
  loading: boolean;
  record: Invitation | null;
  etag: string | null;
  sending: boolean;
  canceling: boolean;
  error: string | null;
  success: string | null;
}

interface RoleEditorState {
  mode: 'create' | 'edit';
  open: boolean;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  record: Role | null;
  etag: string | null;
  error: string | null;
  success: string | null;
}

interface InviteEditorState {
  open: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
}

interface LabelPair {
  id: string;
  key: string;
  value: string;
}


interface RoleUsageMap {
  [roleId: string]: number;
}

type ActiveWorkspaceProps = Omit<ListWorkspaceProps<any>, 'tabs' | 'activeTabId' | 'onTabChange'>;

type AccessDeleteRequest =
  | { kind: 'members'; rows: Membership[] }
  | { kind: 'invitations'; rows: Invitation[] }
  | { kind: 'roles'; rows: Role[] }
  | { kind: 'api-keys'; rows: ApiKey[] };

const backendMissingStatuses = new Set([404, 501, 502, 503]);

type MembershipStatusValue = Membership['status'];
type InvitationStatusValue = Invitation['status'];
type RoleStatusValue = Role['status'];
type PermissionResource = string;
type PermissionVerb = string;

const membershipStatusOrder: MembershipStatusValue[] = ['active', 'suspended'];
const invitationStatusOrder: InvitationStatusValue[] = ['pending', 'accepted', 'revoked', 'expired'];
const roleStatusOrder: RoleStatusValue[] = ['active', 'deprecated'];
const roleVerbOrder: PermissionVerb[] = ['read', 'create', 'update', 'delete', 'execute', 'operate', 'manage'];
const knownPermissionResources: PermissionResource[] = [
  'auth',
  'identity',
  'org',
  'role',
  'membership',
  'invitation',
  'auth_policy',
  'api_key',
  'site',
  'device',
  'connector',
  'connector_cluster',
  'credential',
  'credential_assignment',
  'secret',
  'discovery',
  'discovery_job',
  'discovery_run',
  'discovery_event',
  'topology',
  'chat',
  'chat_thread',
  'chat_message',
  'chat_run',
  'chat_event',
  'delegation',
  'delegation_invite',
  'audit_event',
  'knowledge',
  'knowledge_document',
  'idp_connection',
  'idp_mapping_rule',
  'credential_store',
  'chat_artifact',
  'device_ssh_session',
  'integration_connection',
  'integration_incident',
  'incident',
  'external_incident',
  'incident_source',
  'posture',
  'posture_job',
  'posture_run',
  'posture_finding',
  'posture_summary',
  'posture_report'
];
const permissionResourceAliases: Record<string, PermissionResource> = {
  idpConnection: 'idp_connection',
  roleMapping: 'idp_mapping_rule',
  auditEvent: 'audit_event',
  authPolicy: 'auth_policy',
  apiKey: 'api_key',
  connectorCluster: 'connector_cluster',
  credentialAssignment: 'credential_assignment',
  credentialStore: 'credential_store',
  discoveryJob: 'discovery_job',
  discoveryRun: 'discovery_run',
  discoveryEvent: 'discovery_event',
  chatThread: 'chat_thread',
  chatMessage: 'chat_message',
  chatRun: 'chat_run',
  chatEvent: 'chat_event',
  chatArtifact: 'chat_artifact',
  delegationInvite: 'delegation_invite',
  deviceSshSession: 'device_ssh_session',
  integrationConnection: 'integration_connection',
  integrationIncident: 'integration_incident'
};
const normalizePermissionResource = (resource: string | undefined): PermissionResource => {
  if (!resource) {
    return '';
  }
  return permissionResourceAliases[resource] ?? resource;
};
const normalizePermissionVerbs = (verbs: readonly string[] | undefined): PermissionVerb[] => {
  const normalized = uniqueStringValues(Array.from(verbs ?? []));
  const known = roleVerbOrder.filter((verb) => normalized.includes(verb));
  const extras = normalized.filter((verb) => !known.includes(verb));
  return [...known, ...extras];
};
const collectPermissionResources = (permissions: readonly Permissionschema[] | undefined): PermissionResource[] =>
  uniqueStringValues((permissions ?? []).map((permission) => normalizePermissionResource(permission.resource)));

const missingIdpStatuses = new Set([404, 501, 502, 503]);

const resolveUserName = (user?: Membership['user'], fallback: string = 'Member'): string => {
  if (!user) {
    return fallback;
  }
  const maybeDisplayName = (user as { displayName?: string }).displayName;
  return maybeDisplayName ?? user.name ?? user.email ?? fallback;
};

const resolveUserEmail = (user?: Membership['user']): string | undefined => {
  return user?.email ?? undefined;
};

const defaultMemberFilters: MemberFilters = {
  q: '',
  status: 'all',
  roleSlug: 'all',
  label: ''
};

const defaultInvitationFilters: InvitationFilters = {
  status: 'all',
  q: ''
};

const defaultRoleFilters: RoleFilters = {
  q: '',
  permission: 'all',
  usage: 'all'
};

const defaultApiKeyFilters: ApiKeyFilters = {
  q: '',
  status: 'all'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const randomId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
};

const toLabelPairs = (input?: Record<string, string> | null): LabelPair[] => {
  if (!input) {
    return [];
  }
  return Object.entries(input).map(([key, value]) => ({ id: randomId(), key, value }));
};

const labelPairsToRecord = (pairs: LabelPair[]): Record<string, string> => {
  return pairs.reduce<Record<string, string>>((acc, pair) => {
    const trimmedKey = pair.key.trim();
    if (!trimmedKey) {
      return acc;
    }
    acc[trimmedKey] = pair.value.trim();
    return acc;
  }, {});
};

const formatDateTimeLocalInput = (value?: Date | null): string => {
  if (!value) {
    return '';
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  const year = value.getFullYear();
  const month = pad(value.getMonth() + 1);
  const day = pad(value.getDate());
  const hours = pad(value.getHours());
  const minutes = pad(value.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const haveSameElements = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const setA = new Set(a);
  for (const item of b) {
    if (!setA.has(item)) {
      return false;
    }
  }
  return true;
};

const uniqueStringValues = (values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)));

const filterKnownRoleSummaries = (
  roleSummaries: Membership['rolesResolved'] | undefined,
  _roles: Role[]
): NonNullable<Membership['rolesResolved']> => {
  return roleSummaries ?? [];
};

const deriveMembershipRoleIds = (record: Membership, roles: Role[]): string[] => {
  const recordWithRoleIds = record as Membership & { roleIds?: string[] };
  const explicitRoleIds = uniqueStringValues(recordWithRoleIds.roleIds ?? []);
  if (explicitRoleIds.length > 0) {
    return explicitRoleIds;
  }

  const resolvedRoleIds = uniqueStringValues((record.rolesResolved ?? []).map((role) => role.id));
  if (resolvedRoleIds.length > 0) {
    return resolvedRoleIds;
  }

  if (roles.length === 0 || !record.rolesResolved?.length) {
    return [];
  }

  const roleIdBySlug = new Map<string, string>();
  for (const role of roles) {
    if (!role.id || !role.slug) {
      continue;
    }
    roleIdBySlug.set(role.slug.toLowerCase(), role.id);
  }

  return uniqueStringValues(
    record.rolesResolved.map((role) => {
      if (!role.slug) {
        return undefined;
      }
      return roleIdBySlug.get(role.slug.toLowerCase());
    })
  );
};

const extractErrorMessage = async (error: unknown): Promise<string> => {
  if (error instanceof ResponseError) {
    try {
      const payload = await error.response.clone().json();
      if (payload && typeof payload.detail === 'string') {
        return payload.detail;
      }
    } catch (parseError) {
      if (parseError instanceof Error) {
        return parseError.message;
      }
    }
    return `Request failed with status ${error.response.status}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error while contacting the API.';
};

const AccessIdentityPage = () => {
  const orgId = useActiveOrgId();
  const membershipsApi = useMembershipsApi();
  const invitationsApi = useInvitationsApi();
  const rolesApi = useRolesApi();
  const apiKeysApi = useApiKeysApi();
  const idpConnectionsApi = useIdpConnectionsApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TabId>('members');
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);

  const [memberFilters, setMemberFilters] = useState<MemberFilters>(defaultMemberFilters);
  const [invitationFilters, setInvitationFilters] = useState<InvitationFilters>(defaultInvitationFilters);
  const [roleFilters, setRoleFilters] = useState<RoleFilters>(defaultRoleFilters);
  const [apiKeyFilters, setApiKeyFilters] = useState<ApiKeyFilters>(defaultApiKeyFilters);

  const [idpConnections, setIdpConnections] = useState<IdpConnectionschema[]>([]);
  const [idpLoading, setIdpLoading] = useState(false);
  const [idpError, setIdpError] = useState<string | null>(null);
  const [idpBackendMissing, setIdpBackendMissing] = useState(false);
  const [idpWizardOpen, setIdpWizardOpen] = useState(false);
  const [idpPreferredType, setIdpPreferredType] = useState<'oidc' | 'saml' | undefined>(undefined);
  const [idpDrawerOpen, setIdpDrawerOpen] = useState(false);
  const [idpSelectedId, setIdpSelectedId] = useState<string | null>(null);
  const [idpDrawerInitialTab, setIdpDrawerInitialTab] = useState<ConnectionDrawerTab>('overview');
  const [idpHighlightRowId, setIdpHighlightRowId] = useState<string | number | null>(null);
  const [pendingIdpDeletes, setPendingIdpDeletes] = useState<IdpConnectionschema[] | null>(null);
  const [idpDeleteBusy, setIdpDeleteBusy] = useState(false);
  const [idpDeleteToast, setIdpDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);

  const {
    state: memberPageState,
    setPage: setMemberPage,
    setPageSize: setMemberPageSize,
    reset: resetMemberPagination,
    registerPageMeta: registerMemberPageMeta,
    adjustExactTotal: adjustMemberExactTotal,
    deriveMeta: deriveMemberPaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });
  const {
    state: invitationPageState,
    setPage: setInvitationPage,
    setPageSize: setInvitationPageSize,
    reset: resetInvitationPagination,
    registerPageMeta: registerInvitationPageMeta,
    adjustExactTotal: adjustInvitationExactTotal,
    deriveMeta: deriveInvitationPaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });
  const {
    state: rolePageState,
    setPage: setRolePage,
    setPageSize: setRolePageSize,
    reset: resetRolePagination,
    registerPageMeta: registerRolePageMeta,
    adjustExactTotal: adjustRoleExactTotal,
    deriveMeta: deriveRolePaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });
  const {
    state: apiKeyPageState,
    setPage: setApiKeyPage,
    setPageSize: setApiKeyPageSize,
    reset: resetApiKeyPagination,
    registerPageMeta: registerApiKeyPageMeta,
    adjustExactTotal: adjustApiKeyExactTotal,
    deriveMeta: deriveApiKeyPaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const updateMemberFilters = useCallback(
    (updater: (prev: MemberFilters) => MemberFilters) => {
      setMemberFilters((prev) => updater(prev));
      resetMemberPagination();
    },
    [resetMemberPagination]
  );
  const updateInvitationFilters = useCallback(
    (updater: (prev: InvitationFilters) => InvitationFilters) => {
      setInvitationFilters((prev) => updater(prev));
      resetInvitationPagination();
    },
    [resetInvitationPagination]
  );
  const updateRoleFilters = useCallback(
    (updater: (prev: RoleFilters) => RoleFilters) => {
      setRoleFilters((prev) => updater(prev));
      resetRolePagination();
    },
    [resetRolePagination]
  );
  const updateApiKeyFilters = useCallback(
    (updater: (prev: ApiKeyFilters) => ApiKeyFilters) => {
      setApiKeyFilters((prev) => updater(prev));
      resetApiKeyPagination();
    },
    [resetApiKeyPagination]
  );

  const loadIdpConnections = useCallback(async () => {
    if (!orgId) {
      setIdpConnections([]);
      return;
    }
    setIdpLoading(true);
    setIdpError(null);
    try {
      const response = await idpConnectionsApi.orgsOrgIdIdpConnectionsGetRaw({ orgId, limit: 200 }, { cache: 'no-store' });
      const payload = await response.value();
      setIdpConnections(payload.items ?? []);
      setIdpBackendMissing(false);
    } catch (err: unknown) {
      if (err instanceof ResponseError && missingIdpStatuses.has(err.response.status)) {
        setIdpBackendMissing(true);
        setIdpConnections([]);
      } else {
        const message = err instanceof Error ? err.message : 'Failed to load IdP connections.';
        setIdpError(message);
      }
    } finally {
      setIdpLoading(false);
    }
  }, [idpConnectionsApi, orgId]);

  useEffect(() => {
    void loadIdpConnections();
  }, [loadIdpConnections]);

  const openIdpWizard = useCallback((type?: 'oidc' | 'saml') => {
    setIdpPreferredType(type);
    setIdpWizardOpen(true);
  }, []);

  const clearIntent = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('intent');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeIdpWizard = useCallback(() => {
    setIdpWizardOpen(false);
    setIdpPreferredType(undefined);
    const intent = searchParams.get('intent');
    if (intent === 'new-oidc-connection' || intent === 'new-saml-connection') {
      clearIntent();
    }
  }, [clearIntent, searchParams]);

  const handleIdpCreated = useCallback(
    async (result: ConnectionWizardResult) => {
      await loadIdpConnections();
      const connectionId = result.connection.id ?? null;
      if (result.openDrawer && connectionId) {
        setIdpSelectedId(connectionId);
        setIdpDrawerInitialTab('overview');
        setIdpDrawerOpen(true);
      }
      if (connectionId) {
        setIdpHighlightRowId(connectionId);
      }
    },
    [loadIdpConnections]
  );

  const handleIdpUpdated = useCallback(
    async (connection: IdpConnection) => {
      setIdpHighlightRowId(connection.id ?? null);
      await loadIdpConnections();
    },
    [loadIdpConnections]
  );

  const handleIdpDeleted = useCallback(async (feedback?: { tone: StatusToastTone; message: string }) => {
    if (idpSelectedId) {
      setIdpConnections((current) => current.filter((connection) => connection.id !== idpSelectedId));
    }
    setIdpDrawerOpen(false);
    setIdpSelectedId(null);
    setIdpHighlightRowId(null);
    if (feedback) {
      setIdpDeleteToast(feedback);
    }
    await loadIdpConnections();
  }, [idpSelectedId, loadIdpConnections]);

  const openIdpDrawer = useCallback((connection: IdpConnectionschema, tab: ConnectionDrawerTab = 'overview') => {
    if (!connection.id) {
      return;
    }
    setIdpSelectedId(connection.id);
    setIdpHighlightRowId(connection.id);
    setIdpDrawerInitialTab(tab);
    setIdpDrawerOpen(true);
  }, []);

  const renderIdpRowActions = useCallback(
    (row: IdpConnectionschema) => {
      const disabled = !row.id;
      return (
        <>
          <button
            type="button"
            disabled={disabled}
            aria-label="Edit connection"
            title="Edit connection"
            onClick={(event) => {
              event.stopPropagation();
              openIdpDrawer(row, 'overview');
            }}
          >
            <PencilIcon size={16} />
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-label="Open checklist"
            title="Open checklist"
            onClick={(event) => {
              event.stopPropagation();
              openIdpDrawer(row, 'checklist');
            }}
          >
            <LayersIcon size={16} />
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-label="Run test"
            title="Run test"
            onClick={(event) => {
              event.stopPropagation();
              openIdpDrawer(row, 'test');
            }}
          >
            <ActivityIcon size={16} />
          </button>
        </>
      );
    },
    [openIdpDrawer]
  );

  const memberFiltersActive = useMemo(() => {
    return (
      memberFilters.q.trim().length > 0 ||
      memberFilters.status !== defaultMemberFilters.status ||
      memberFilters.roleSlug !== defaultMemberFilters.roleSlug ||
      memberFilters.label.trim().length > 0
    );
  }, [memberFilters]);

  const invitationFiltersActive = useMemo(() => {
    return invitationFilters.status !== defaultInvitationFilters.status || invitationFilters.q.trim().length > 0;
  }, [invitationFilters]);

  const roleFiltersActive = useMemo(() => {
    return (
      roleFilters.q.trim().length > 0 ||
      roleFilters.permission !== defaultRoleFilters.permission ||
      roleFilters.usage !== defaultRoleFilters.usage
    );
  }, [roleFilters]);

  const apiKeyFiltersActive = useMemo(() => {
    return apiKeyFilters.status !== defaultApiKeyFilters.status || apiKeyFilters.q.trim().length > 0;
  }, [apiKeyFilters]);

  const [membersState, setMembersState] = useState<MembershipListState>({ items: [], loading: false, error: null, backendMissing: false });
  const [invitationsState, setInvitationsState] = useState<InvitationsListState>({ items: [], loading: false, error: null, backendMissing: false });
  const [rolesState, setRolesState] = useState<RolesListState>({ items: [], loading: false, error: null, backendMissing: false });
  const [apiKeysState, setApiKeysState] = useState<ApiKeysListState>({ items: [], loading: false, error: null, backendMissing: false });

  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string | null>(null);
  const [roleUsageMap, setRoleUsageMap] = useState<RoleUsageMap>({});
  const [pendingInvitesCount, setPendingInvitesCount] = useState<number | null>(null);
  const [activeApiKeysCount, setActiveApiKeysCount] = useState<number | null>(null);

  const [memberDetail, setMemberDetail] = useState<MembershipDetailState>({
    open: false,
    loading: false,
    saving: false,
    revoking: false,
    record: null,
    etag: null,
    error: null,
    success: null
  });

  const [invitationDetail, setInvitationDetail] = useState<InvitationDetailState>({
    open: false,
    loading: false,
    record: null,
    etag: null,
    sending: false,
    canceling: false,
    error: null,
    success: null
  });

  const [roleEditor, setRoleEditor] = useState<RoleEditorState>({
    mode: 'create',
    open: false,
    loading: false,
    saving: false,
    deleting: false,
    record: null,
    etag: null,
    error: null,
    success: null
  });

  const [inviteEditor, setInviteEditor] = useState<InviteEditorState>({ open: false, saving: false, error: null, success: null });
  const [apiKeyDetail, setApiKeyDetail] = useState<ApiKeyDetailState>({
    open: false,
    loading: false,
    saving: false,
    revoking: false,
    record: null,
    etag: null,
    error: null,
    success: null
  });
  const [apiKeyCreate, setApiKeyCreate] = useState<ApiKeyCreateState>({ open: false, saving: false, error: null, response: null });
  const [memberActionBusy, setMemberActionBusy] = useState<string | null>(null);
  const [invitationActionBusy, setInvitationActionBusy] = useState<string | null>(null);
  const [apiKeyActionBusy, setApiKeyActionBusy] = useState<string | null>(null);
  const [pendingAccessDelete, setPendingAccessDelete] = useState<AccessDeleteRequest | null>(null);
  const [accessDeleteBusy, setAccessDeleteBusy] = useState(false);
  const [accessDeleteToast, setAccessDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const memberVisibleRowCountRef = useRef(0);
  const invitationVisibleRowCountRef = useRef(0);
  const roleVisibleRowCountRef = useRef(0);
  const apiKeyVisibleRowCountRef = useRef(0);

  const addAlert = useCallback((tone: AlertTone, message: string, title?: string) => {
    setAlerts((prev) => [{ id: randomId(), tone, title, message }, ...prev]);
  }, []);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const resetDataForMissingOrg = useCallback(() => {
    setMembersState({ items: [], loading: false, error: null, backendMissing: false });
    setInvitationsState({ items: [], loading: false, error: null, backendMissing: false });
    setRolesState({ items: [], loading: false, error: null, backendMissing: false });
    setApiKeysState({ items: [], loading: false, error: null, backendMissing: false });
    setSelectedMemberId(null);
    setSelectedInvitationId(null);
    setSelectedRoleId(null);
    setSelectedApiKeyId(null);
    setMemberDetail({ open: false, loading: false, saving: false, revoking: false, record: null, etag: null, error: null, success: null });
    setInvitationDetail({ open: false, loading: false, record: null, etag: null, sending: false, canceling: false, error: null, success: null });
    setRoleEditor({ mode: 'create', open: false, loading: false, saving: false, deleting: false, record: null, etag: null, error: null, success: null });
    setInviteEditor({ open: false, saving: false, error: null, success: null });
    setApiKeyDetail({ open: false, loading: false, saving: false, revoking: false, record: null, etag: null, error: null, success: null });
    setApiKeyCreate({ open: false, saving: false, error: null, response: null });
    setPendingAccessDelete(null);
    setAccessDeleteBusy(false);
    setAccessDeleteToast(null);
    updateMemberFilters(() => ({ ...defaultMemberFilters }));
    updateInvitationFilters(() => ({ ...defaultInvitationFilters }));
    updateRoleFilters(() => ({ ...defaultRoleFilters }));
    updateApiKeyFilters(() => ({ ...defaultApiKeyFilters }));
    setPendingInvitesCount(null);
    setActiveApiKeysCount(null);
  }, [updateApiKeyFilters, updateInvitationFilters, updateMemberFilters, updateRoleFilters]);

  const resolveCursorParams = (cursor: CursorPaginationState['cursor']) => {
    if (cursor.direction === 'after') {
      return { after: cursor.value, before: undefined };
    }
    if (cursor.direction === 'before') {
      return { after: undefined, before: cursor.value };
    }
    return { after: undefined, before: undefined };
  };

  const loadMembers = useCallback(async () => {
    if (!orgId) {
      resetDataForMissingOrg();
      return;
    }
    setMembersState((prev) => ({ ...prev, loading: true, error: null, backendMissing: false }));
    try {
      const cursorParams = resolveCursorParams(memberPageState.cursor);
      const response = await membershipsApi.orgsOrgIdMembershipsGetRaw({
        orgId,
        limit: memberPageState.pageSize,
        ...cursorParams,
        status: memberFilters.status !== 'all' ? (memberFilters.status as Membership['status']) : undefined,
        label: memberFilters.label.trim() || undefined,
        roleSlug: memberFilters.roleSlug !== 'all' ? memberFilters.roleSlug : undefined,
        q: memberFilters.q.trim() || undefined,
        expand: ['user']
      });
      const payload = await response.value();
      registerMemberPageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
      setMembersState({ items: payload.items ?? [], loading: false, error: null, backendMissing: false });
    } catch (error) {
      if (error instanceof ResponseError && backendMissingStatuses.has(error.response.status)) {
        setMembersState({ items: [], loading: false, error: null, backendMissing: true });
        return;
      }
      const message = await extractErrorMessage(error);
      setMembersState({ items: [], loading: false, error: message, backendMissing: false });
    }
  }, [
    memberFilters.label,
    memberFilters.q,
    memberFilters.roleSlug,
    memberFilters.status,
    memberPageState.cursor,
    memberPageState.pageSize,
    membershipsApi,
    orgId,
    registerMemberPageMeta,
    resetDataForMissingOrg
  ]);

  const refreshPendingInvitesCount = useCallback(async () => {
    if (!orgId) {
      setPendingInvitesCount(null);
      return;
    }
    try {
      const response = await invitationsApi.orgsOrgIdInvitationsGetRaw({ orgId, limit: 1, status: 'pending' });
      const payload = await response.value();
      setPendingInvitesCount(payload.meta?.exactTotal ?? payload.items?.length ?? 0);
    } catch {
      setPendingInvitesCount(null);
    }
  }, [invitationsApi, orgId]);

  const refreshActiveApiKeysCount = useCallback(async () => {
    if (!orgId) {
      setActiveApiKeysCount(null);
      return;
    }
    try {
      const response = await apiKeysApi.orgsOrgIdApiKeysGetRaw({ orgId, limit: 1, status: ApiKeyStatusEnum.Active });
      const payload = await response.value();
      setActiveApiKeysCount(payload.meta?.exactTotal ?? payload.items?.length ?? 0);
    } catch {
      setActiveApiKeysCount(null);
    }
  }, [apiKeysApi, orgId]);

  const loadInvitations = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setInvitationsState((prev) => ({ ...prev, loading: true, error: null, backendMissing: false }));
    try {
      const cursorParams = resolveCursorParams(invitationPageState.cursor);
      const requestParams: OrgsOrgIdInvitationsGetRequest = {
        orgId,
        limit: invitationPageState.pageSize,
        ...cursorParams
      };
      if (invitationFilters.status !== 'all') {
        requestParams.status = invitationFilters.status;
      }
      if (invitationFilters.q.trim().length > 0) {
        requestParams.q = invitationFilters.q.trim();
      }
      const response = await invitationsApi.orgsOrgIdInvitationsGetRaw(requestParams);
      const payload = await response.value();
      registerInvitationPageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
      setInvitationsState({ items: payload.items ?? [], loading: false, error: null, backendMissing: false });
      if (invitationFilters.status === 'pending') {
        setPendingInvitesCount(payload.meta?.exactTotal ?? payload.items?.length ?? 0);
      } else {
        refreshPendingInvitesCount();
      }
    } catch (error) {
      if (error instanceof ResponseError && backendMissingStatuses.has(error.response.status)) {
        setInvitationsState({ items: [], loading: false, error: null, backendMissing: true });
        return;
      }
      const message = await extractErrorMessage(error);
      setInvitationsState({ items: [], loading: false, error: message, backendMissing: false });
    }
  }, [
    invitationFilters.q,
    invitationFilters.status,
    invitationPageState.cursor,
    invitationPageState.pageSize,
    invitationsApi,
    orgId,
    refreshPendingInvitesCount,
    registerInvitationPageMeta
  ]);

  const loadRoles = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setRolesState((prev) => ({ ...prev, loading: true, error: null, backendMissing: false }));
    try {
      const cursorParams = resolveCursorParams(rolePageState.cursor);
      const response = await rolesApi.orgsOrgIdRolesGetRaw({
        orgId,
        limit: rolePageState.pageSize,
        ...cursorParams,
        q: roleFilters.q.trim() || undefined,
        permission: roleFilters.permission !== 'all' ? roleFilters.permission : undefined,
        usage: roleFilters.usage !== 'all' ? roleFilters.usage : undefined
      } as any);
      const payload = await response.value();
      registerRolePageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
      setRolesState({ items: payload.items ?? [], loading: false, error: null, backendMissing: false });
    } catch (error) {
      if (error instanceof ResponseError && backendMissingStatuses.has(error.response.status)) {
        setRolesState({ items: [], loading: false, error: null, backendMissing: true });
        return;
      }
      const message = await extractErrorMessage(error);
      setRolesState({ items: [], loading: false, error: message, backendMissing: false });
    }
  }, [orgId, registerRolePageMeta, roleFilters.q, roleFilters.permission, roleFilters.usage, rolePageState.cursor, rolePageState.pageSize, rolesApi]);

  const loadApiKeys = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setApiKeysState((prev) => ({ ...prev, loading: true, error: null, backendMissing: false }));
    try {
      const cursorParams = resolveCursorParams(apiKeyPageState.cursor);
      const requestParams: OrgsOrgIdApiKeysGetRequest = {
        orgId,
        limit: apiKeyPageState.pageSize,
        ...cursorParams
      };
      if (apiKeyFilters.status !== 'all') {
        requestParams.status = apiKeyFilters.status;
      }
      if (apiKeyFilters.q.trim().length > 0) {
        requestParams.q = apiKeyFilters.q.trim();
      }
      const response = await apiKeysApi.orgsOrgIdApiKeysGetRaw(requestParams);
      const payload = await response.value();
      registerApiKeyPageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
      setApiKeysState({ items: payload.items ?? [], loading: false, error: null, backendMissing: false });
      if (apiKeyFilters.status === ApiKeyStatusEnum.Active) {
        setActiveApiKeysCount(payload.meta?.exactTotal ?? payload.items?.length ?? 0);
      } else {
        refreshActiveApiKeysCount();
      }
    } catch (error) {
      if (error instanceof ResponseError && backendMissingStatuses.has(error.response.status)) {
        setApiKeysState({ items: [], loading: false, error: null, backendMissing: true });
        return;
      }
      const message = await extractErrorMessage(error);
      setApiKeysState({ items: [], loading: false, error: message, backendMissing: false });
    }
  }, [
    apiKeyFilters.q,
    apiKeyFilters.status,
    apiKeyPageState.cursor,
    apiKeyPageState.pageSize,
    apiKeysApi,
    orgId,
    refreshActiveApiKeysCount,
    registerApiKeyPageMeta
  ]);

  const [roleCatalogVersion, setRoleCatalogVersion] = useState(0);
  const refreshFromChangefeed = useCallback(() => {
    setRoleCatalogVersion(value => value + 1);
    if (!orgId) {
      return;
    }
    loadMembers();
    loadInvitations();
    loadRoles();
    loadApiKeys();
    refreshPendingInvitesCount();
    refreshActiveApiKeysCount();
  }, [
    loadApiKeys,
    loadInvitations,
    loadMembers,
    loadRoles,
    orgId,
    refreshActiveApiKeysCount,
    refreshPendingInvitesCount
  ]);

  useOrgChangefeedEffect(['memberships', 'invitations', 'roles', 'api_keys'], refreshFromChangefeed, [refreshFromChangefeed]);

  useEffect(() => { if (orgId) void loadMembers(); else resetDataForMissingOrg(); }, [orgId, loadMembers, resetDataForMissingOrg]);
  useEffect(() => { if (orgId) void loadInvitations(); }, [orgId, loadInvitations]);
  useEffect(() => { if (orgId) void loadRoles(); }, [orgId, loadRoles]);
  useEffect(() => { if (orgId) void loadApiKeys(); }, [orgId, loadApiKeys]);

  useEffect(() => {
    refreshPendingInvitesCount();
  }, [refreshPendingInvitesCount]);

  useEffect(() => {
    refreshActiveApiKeysCount();
  }, [refreshActiveApiKeysCount]);

  const fetchMemberDetail = useCallback(
    async (membershipId: string) => {
      if (!orgId) {
        throw new Error('Missing org id');
      }
      const response = await membershipsApi.orgsOrgIdMembershipsMembershipIdGetRaw({ orgId, membershipId, expand: ['user'] });
      const rawRecordPromise = response.raw
        .clone()
        .json()
        .catch(() => null) as Promise<{ roleIds?: unknown } | null>;
      const record = await response.value();
      const rawRecord = await rawRecordPromise;
      const roleIds =
        rawRecord && Array.isArray(rawRecord.roleIds)
          ? rawRecord.roleIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          : undefined;
      const etag = response.raw.headers.get('ETag') ?? null;
      return { record: roleIds ? { ...record, roleIds } : record, etag };
    },
    [membershipsApi, orgId]
  );

  const fetchInvitationDetail = useCallback(
    async (invitationId: string) => {
      if (!orgId) {
        throw new Error('Missing org id');
      }
      const response = await invitationsApi.orgsOrgIdInvitationsInvitationIdGetRaw({ orgId, invitationId });
      const record = await response.value();
      const etag = response.raw.headers.get('ETag') ?? null;
      return { record, etag };
    },
    [invitationsApi, orgId]
  );

  const fetchRoleDetail = useCallback(
    async (roleId: string) => {
      if (!orgId) {
        throw new Error('Missing org id');
      }
      const response = await rolesApi.orgsOrgIdRolesRoleIdGetRaw({ orgId, roleId });
      const record = await response.value();
      const etag = response.raw.headers.get('ETag') ?? null;
      return { record, etag };
    },
    [rolesApi, orgId]
  );

  const fetchApiKeyDetail = useCallback(
    async (apiKeyId: string) => {
      if (!orgId) {
        throw new Error('Missing org id');
      }
      const response = await apiKeysApi.orgsOrgIdApiKeysApiKeyIdGetRaw({ orgId, apiKeyId });
      const record = await response.value();
      const etag = response.raw.headers.get('ETag') ?? null;
      return { record, etag };
    },
    [apiKeysApi, orgId]
  );

  const handleMemberSelect = useCallback(
    async (membership: Membership) => {
      if (!membership.id) {
        return;
      }
      setActiveTab('members');
      setSelectedMemberId(membership.id);
      setMemberDetail((prev) => ({ ...prev, open: true, loading: true, error: null, success: null }));
      try {
        const { record, etag } = await fetchMemberDetail(membership.id);
        setMemberDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record,
          etag,
          error: null,
          success: null
        });
      } catch (error) {
        const message = await extractErrorMessage(error);
        setMemberDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record: null,
          etag: null,
          error: message,
          success: null
        });
      }
    },
    [fetchMemberDetail]
  );

  const handleInvitationSelect = useCallback(
    async (invitation: Invitation) => {
      if (!invitation.id) {
        return;
      }
      setActiveTab('invitations');
      setSelectedInvitationId(invitation.id);
      setInvitationDetail((prev) => ({ ...prev, open: true, loading: true, error: null, success: null }));
      try {
        const { record, etag } = await fetchInvitationDetail(invitation.id);
        setInvitationDetail({
          open: true,
          loading: false,
          record,
          etag,
          sending: false,
          canceling: false,
          error: null,
          success: null
        });
      } catch (error) {
        const message = await extractErrorMessage(error);
        setInvitationDetail({
          open: true,
          loading: false,
          record: null,
          etag: null,
          sending: false,
          canceling: false,
          error: message,
          success: null
        });
      }
    },
    [fetchInvitationDetail]
  );

  const openRoleEditor = useCallback(
    async (role: Role, mode: 'create' | 'edit') => {
      setActiveTab('roles');
      if (mode === 'create') {
        setRoleEditor({
          mode: 'create',
          open: true,
          loading: false,
          saving: false,
          deleting: false,
          record: null,
          etag: null,
          error: null,
          success: null
        });
        return;
      }
      if (!role.id) {
        return;
      }
      setSelectedRoleId(role.id);
      setRoleEditor({
        mode: 'edit',
        open: true,
        loading: true,
        saving: false,
        deleting: false,
        record: null,
        etag: null,
        error: null,
        success: null
      });
      try {
        const { record, etag } = await fetchRoleDetail(role.id);
        setRoleEditor({
          mode: 'edit',
          open: true,
          loading: false,
          saving: false,
          deleting: false,
          record,
          etag,
          error: null,
          success: null
        });
      } catch (error) {
        const message = await extractErrorMessage(error);
        setRoleEditor({
          mode: 'edit',
          open: true,
          loading: false,
          saving: false,
          deleting: false,
          record: null,
          etag: null,
          error: message,
          success: null
        });
      }
    },
    [fetchRoleDetail]
  );

  const handleRoleSelect = useCallback(
    async (role: Role) => {
      await openRoleEditor(role, 'edit');
    },
    [openRoleEditor]
  );

  const handleApiKeySelect = useCallback(
    async (apiKey: ApiKey) => {
      if (!apiKey.id) {
        return;
      }
      setActiveTab('api-keys');
      setSelectedApiKeyId(apiKey.id);
      setApiKeyDetail((prev) => ({
        ...prev,
        open: true,
        loading: true,
        saving: false,
        revoking: false,
        error: null,
        success: null
      }));
      try {
        const { record, etag } = await fetchApiKeyDetail(apiKey.id);
        setApiKeyDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record,
          etag,
          error: null,
          success: null
        });
      } catch (error) {
        const message = await extractErrorMessage(error);
        setApiKeyDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record: null,
          etag: null,
          error: message,
          success: null
        });
      }
    },
    [fetchApiKeyDetail]
  );

  const memberDetailId = memberDetail.record?.id ?? null;
  const memberDetailEtag = memberDetail.etag;
  const apiKeyDetailId = apiKeyDetail.record?.id ?? null;
  const apiKeyDetailEtag = apiKeyDetail.etag;
  const resolveMembershipEtag = useCallback(
    async (membershipId: string, fallback?: string | null) => {
      const direct = fallback?.trim();
      if (direct) {
        return direct;
      }
      const { etag } = await fetchMemberDetail(membershipId);
      return etag;
    },
    [fetchMemberDetail]
  );

  const resolveInvitationEtag = useCallback(
    async (invitationId: string, fallback?: string | null) => {
      const direct = fallback?.trim();
      if (direct) {
        return direct;
      }
      const { etag } = await fetchInvitationDetail(invitationId);
      return etag;
    },
    [fetchInvitationDetail]
  );

  const createIfMatchInitOverride = useCallback((etag?: string | null) => {
    const resolved = etag?.trim();
    if (!resolved) {
      return undefined;
    }
    return async ({ init }: { init: RequestInit }) => {
      const headers = new Headers(init.headers ?? undefined);
      headers.set('If-Match', resolved);
      return { headers };
    };
  }, []);

  const handleMemberUpdate = useCallback(
    async (update: MembershipUpdate) => {
      if (!orgId || !memberDetailId) {
        return;
      }
      setMemberDetail((prev) => ({ ...prev, saving: true, error: null, success: null }));
      try {
        const etag = await resolveMembershipEtag(memberDetailId, memberDetailEtag);
        const response = await membershipsApi.orgsOrgIdMembershipsMembershipIdPatchRaw({
          orgId,
          membershipId: memberDetailId,
          membershipUpdate: update,
          ifMatch: etag ?? undefined
        });
        const record = await response.value();
        const responseEtag = response.raw.headers.get('ETag') ?? null;
        setMemberDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record,
          etag: responseEtag,
          error: null,
          success: 'Membership updated successfully.'
        });
        setMembersState((prev) => ({ ...prev, items: prev.items.map((item) => (item.id === record.id ? record : item)) }));
        addAlert('success', `${resolveUserName(record.user, 'Member')} updated.`);
      } catch (error) {
        const message = await extractErrorMessage(error);
        setMemberDetail((prev) => ({ ...prev, saving: false, error: message }));
        if (error instanceof ResponseError && error.response.status === 412) {
          try {
            const { record, etag } = await fetchMemberDetail(memberDetailId);
            setMemberDetail({
              open: true,
              loading: false,
              saving: false,
              revoking: false,
              record,
              etag,
              error: message,
              success: null
            });
          } catch (refreshError) {
            const refreshMessage = await extractErrorMessage(refreshError);
            setMemberDetail((prev) => ({ ...prev, error: `${message} (${refreshMessage})` }));
          }
        }
      }
    },
    [addAlert, fetchMemberDetail, memberDetailEtag, memberDetailId, membershipsApi, orgId, resolveMembershipEtag]
  );

  const handleMemberRevoke = useCallback(() => {
    if (!orgId || !memberDetail.record || accessDeleteBusy) {
      return;
    }
    setPendingAccessDelete({ kind: 'members', rows: [memberDetail.record] });
  }, [accessDeleteBusy, memberDetail.record, orgId]);

  const invitationDetailId = invitationDetail.record?.id ?? null;

  const handleInvitationResend = useCallback(async () => {
    if (!orgId || !invitationDetailId) {
      return;
    }
    setInvitationDetail((prev) => ({ ...prev, sending: true, error: null, success: null }));
    try {
      await invitationsApi.orgsOrgIdInvitationsInvitationIdSendPost({ orgId, invitationId: invitationDetailId });
      setInvitationDetail((prev) => ({ ...prev, sending: false, success: 'Invitation resent.' }));
      addAlert('success', 'Invitation resent.');
      await loadInvitations();
    } catch (error) {
      const message = await extractErrorMessage(error);
      setInvitationDetail((prev) => ({ ...prev, sending: false, error: message }));
    }
  }, [addAlert, invitationDetailId, invitationsApi, loadInvitations, orgId]);

  const handleInvitationCancel = useCallback(() => {
    if (!orgId || !invitationDetail.record || accessDeleteBusy) {
      return;
    }
    setPendingAccessDelete({ kind: 'invitations', rows: [invitationDetail.record] });
  }, [accessDeleteBusy, invitationDetail.record, orgId]);

  const handleInviteSubmit = useCallback(
    async (payload: InvitationCreate) => {
      if (!orgId) {
        return;
      }
      setInviteEditor((prev) => ({ ...prev, saving: true, error: null, success: null }));
      try {
        const response = await invitationsApi.orgsOrgIdInvitationsPostRaw({ orgId, invitationCreate: payload });
        await response.value();
        setInviteEditor({ open: false, saving: false, error: null, success: null });
        clearIntent();
        addAlert('success', 'Invitation sent.');
        await loadInvitations();
      } catch (error) {
        const message = await extractErrorMessage(error);
        setInviteEditor((prev) => ({ ...prev, saving: false, error: message }));
      }
    },
    [addAlert, clearIntent, invitationsApi, loadInvitations, orgId]
  );

  const roleEditorId = roleEditor.record?.id ?? null;
  const roleEditorMode = roleEditor.mode;
  const roleEditorEtag = roleEditor.etag;

  const handleRoleSave = useCallback(
    async (payload: { mode: 'create'; body: RoleCreate } | { mode: 'edit'; body: RoleUpdate }) => {
      if (!orgId) {
        return;
      }
      setRoleEditor((prev) => ({ ...prev, saving: true, error: null, success: null }));
      try {
        if (payload.mode === 'create') {
          const response = await rolesApi.orgsOrgIdRolesPostRaw({ orgId, roleCreate: payload.body });
          const record = await response.value();
          setRoleEditor({
            mode: 'edit',
            open: true,
            loading: false,
            saving: false,
            deleting: false,
            record,
            etag: response.raw.headers.get('ETag') ?? null,
            error: null,
            success: 'Role created.'
          });
          addAlert('success', 'Role created.');
        } else if (payload.mode === 'edit' && roleEditorId) {
          const response = await rolesApi.orgsOrgIdRolesRoleIdPatchRaw({
            orgId,
            roleId: roleEditorId,
            roleUpdate: payload.body,
            ifMatch: roleEditorEtag ?? undefined
          });
          const record = await response.value();
          setRoleEditor({
            mode: 'edit',
            open: true,
            loading: false,
            saving: false,
            deleting: false,
            record,
            etag: response.raw.headers.get('ETag') ?? null,
            error: null,
            success: 'Role updated.'
          });
          addAlert('success', 'Role updated.');
        }
        setRoleCatalogVersion(value => value + 1);
        await loadRoles();
        await loadMembers();
        await loadInvitations();
      } catch (error) {
        const message = await extractErrorMessage(error);
        setRoleEditor((prev) => ({ ...prev, saving: false, error: message }));
        if (error instanceof ResponseError && error.response.status === 412 && roleEditorId) {
          try {
            const { record, etag } = await fetchRoleDetail(roleEditorId);
            setRoleEditor((prev) => ({ ...prev, record, etag }));
          } catch (refreshError) {
            const refreshMessage = await extractErrorMessage(refreshError);
            setRoleEditor((prev) => ({ ...prev, error: `${message} (${refreshMessage})` }));
          }
        }
      }
    },
    [addAlert, fetchRoleDetail, loadInvitations, loadMembers, loadRoles, orgId, roleEditorEtag, roleEditorId, rolesApi]
  );

  const handleRoleDelete = useCallback(() => {
    if (!orgId || !roleEditor.record || accessDeleteBusy) {
      return;
    }
    setPendingAccessDelete({ kind: 'roles', rows: [roleEditor.record] });
  }, [accessDeleteBusy, orgId, roleEditor.record]);

  const handleApiKeyCreate = useCallback(
    async (payload: ApiKeyCreate) => {
      if (!orgId) {
        return;
      }
      setApiKeyCreate((prev) => ({ ...prev, saving: true, error: null }));
      try {
        const response = await apiKeysApi.orgsOrgIdApiKeysPostRaw({ orgId, apiKeyCreate: payload });
        const record = await response.value();
        setApiKeyCreate({ open: true, saving: false, error: null, response: record });
        await loadApiKeys();
        addAlert('success', `API key ${record.name} created.`);
      } catch (error) {
        const message = await extractErrorMessage(error);
        setApiKeyCreate((prev) => ({ ...prev, saving: false, error: message }));
      }
    },
    [addAlert, apiKeysApi, loadApiKeys, orgId]
  );

  const handleApiKeyUpdate = useCallback(
    async (update: ApiKeyUpdate) => {
      if (!orgId || !apiKeyDetailId) {
        return;
      }
      setApiKeyDetail((prev) => ({ ...prev, saving: true, error: null, success: null }));
      try {
        const response = await apiKeysApi.orgsOrgIdApiKeysApiKeyIdPatchRaw({
          orgId,
          apiKeyId: apiKeyDetailId,
          apiKeyUpdate: update,
          ifMatch: apiKeyDetailEtag ?? undefined
        });
        const record = await response.value();
        const etag = response.raw.headers.get('ETag') ?? null;
        setApiKeyDetail({
          open: true,
          loading: false,
          saving: false,
          revoking: false,
          record,
          etag,
          error: null,
          success: 'API key updated.'
        });
        await loadApiKeys();
        addAlert('success', `API key ${record.name} updated.`);
      } catch (error) {
        const message = await extractErrorMessage(error);
        setApiKeyDetail((prev) => ({ ...prev, saving: false, error: message }));
        if (error instanceof ResponseError && error.response.status === 412 && apiKeyDetailId) {
          try {
            const { record, etag } = await fetchApiKeyDetail(apiKeyDetailId);
            setApiKeyDetail({
              open: true,
              loading: false,
              saving: false,
              revoking: false,
              record,
              etag,
              error: message,
              success: null
            });
          } catch (refreshError) {
            const refreshMessage = await extractErrorMessage(refreshError);
            setApiKeyDetail((prev) => ({ ...prev, error: `${message} (${refreshMessage})` }));
          }
        }
      }
    },
    [addAlert, apiKeysApi, apiKeyDetailEtag, apiKeyDetailId, fetchApiKeyDetail, loadApiKeys, orgId]
  );

  const handleApiKeyRevoke = useCallback(() => {
    if (!orgId || !apiKeyDetail.record || accessDeleteBusy) {
      return;
    }
    setPendingAccessDelete({ kind: 'api-keys', rows: [apiKeyDetail.record] });
  }, [accessDeleteBusy, apiKeyDetail.record, orgId]);

  const handleInviteDrawerOpen = useCallback(() => {
    setInviteEditor({ open: true, saving: false, error: null, success: null });
  }, []);

  const handleRoleCreate = useCallback(() => {
    openRoleEditor({} as Role, 'create');
  }, [openRoleEditor]);

  const handleApiKeyCreateOpen = useCallback(() => {
    setApiKeyCreate({ open: true, saving: false, error: null, response: null });
  }, []);

  const handleMemberStatusQuick = useCallback(
    async (member: Membership, nextStatus: MembershipStatusValue) => {
      if (!orgId || !member.id) {
        return;
      }
      setMemberActionBusy(member.id);
      try {
        const etag = await resolveMembershipEtag(member.id);
        const response = await membershipsApi.orgsOrgIdMembershipsMembershipIdPatchRaw({
          orgId,
          membershipId: member.id,
          membershipUpdate: { status: nextStatus },
          ifMatch: etag ?? undefined
        });
        const record = await response.value();
        setMembersState((prev) => ({
          ...prev,
          items: prev.items.map((item) => (item.id === record.id ? record : item))
        }));
        addAlert('success', `Membership ${resolveUserName(record.user, 'Member')} ${nextStatus === 'suspended' ? 'suspended' : 'activated'}.`);
      } catch (error) {
        const message = await extractErrorMessage(error);
        addAlert('danger', message);
      } finally {
        setMemberActionBusy(null);
      }
    },
    [addAlert, membershipsApi, orgId, resolveMembershipEtag]
  );

  const handleMembersBulkRevoke = useCallback(
    (rows: Membership[]) => {
      if (!orgId || rows.length === 0 || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'members', rows });
    },
    [accessDeleteBusy, orgId]
  );

  const handleRolesBulkDelete = useCallback(
    (rows: Role[]) => {
      if (!orgId || rows.length === 0 || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'roles', rows });
    },
    [accessDeleteBusy, orgId]
  );

  const handleInvitationResendQuick = useCallback(
    async (invitation: Invitation) => {
      if (!orgId || !invitation.id) {
        return;
      }
      setInvitationActionBusy(invitation.id);
      try {
        await invitationsApi.orgsOrgIdInvitationsInvitationIdSendPost({ orgId, invitationId: invitation.id });
        addAlert('success', `Invitation to ${invitation.email} resent.`);
        await loadInvitations();
      } catch (error) {
        addAlert('danger', await extractErrorMessage(error));
      } finally {
        setInvitationActionBusy(null);
      }
    },
    [addAlert, invitationsApi, loadInvitations, orgId]
  );

  const handleInvitationCancelQuick = useCallback(
    (invitation: Invitation) => {
      if (!orgId || !invitation.id || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'invitations', rows: [invitation] });
    },
    [accessDeleteBusy, orgId]
  );

  const handleInvitationsBulkCancel = useCallback(
    (rows: Invitation[]) => {
      if (!orgId || rows.length === 0 || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'invitations', rows });
    },
    [accessDeleteBusy, orgId]
  );

  const revokeApiKeyById = useCallback(
    async (apiKeyId: string) => {
      if (!orgId) {
        return;
      }
      const { etag } = await fetchApiKeyDetail(apiKeyId);
      await apiKeysApi.orgsOrgIdApiKeysApiKeyIdDeleteRaw({
        orgId,
        apiKeyId,
        ifMatch: etag ?? undefined
      });
    },
    [apiKeysApi, fetchApiKeyDetail, orgId]
  );

  const handleApiKeyRevokeQuick = useCallback(
    (apiKey: ApiKey) => {
      if (!orgId || !apiKey.id || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'api-keys', rows: [apiKey] });
    },
    [accessDeleteBusy, orgId]
  );

  const handleApiKeysBulkRevoke = useCallback(
    (rows: ApiKey[]) => {
      if (!orgId || rows.length === 0 || accessDeleteBusy) {
        return;
      }
      setPendingAccessDelete({ kind: 'api-keys', rows });
    },
    [accessDeleteBusy, orgId]
  );

  const confirmAccessDelete = useCallback(async () => {
    const request = pendingAccessDelete;
    if (!orgId || !request || request.rows.length === 0 || accessDeleteBusy) {
      return;
    }

    setAccessDeleteBusy(true);
    const settleDeletes = <T extends { id?: string | null }>(rows: T[], deleteRow: (row: T & { id: string }) => Promise<void>) =>
      Promise.allSettled(
        rows.map(async (row) => {
          if (!row.id) {
            throw new Error('Missing resource identifier.');
          }
          await deleteRow(row as T & { id: string });
          return row.id;
        })
      );

    let results: PromiseSettledResult<string>[];
    if (request.kind === 'members') {
      results = await settleDeletes(request.rows, async (member) => {
        const etag = await resolveMembershipEtag(member.id);
        await membershipsApi.orgsOrgIdMembershipsMembershipIdDeleteRaw(
          { orgId, membershipId: member.id },
          createIfMatchInitOverride(etag)
        );
      });
    } else if (request.kind === 'invitations') {
      results = await settleDeletes(request.rows, async (invitation) => {
        const etag = await resolveInvitationEtag(invitation.id);
        if (!etag) {
          throw new Error('Missing ETag for invitation. Refresh and try again.');
        }
        await invitationsApi.orgsOrgIdInvitationsInvitationIdDeleteRaw({
          orgId,
          invitationId: invitation.id,
          ifMatch: etag
        });
      });
    } else if (request.kind === 'roles') {
      results = await settleDeletes(request.rows, async (role) => {
        const { etag } = await fetchRoleDetail(role.id);
        await rolesApi.orgsOrgIdRolesRoleIdDeleteRaw({ orgId, roleId: role.id, ifMatch: etag ?? undefined });
      });
    } else {
      results = await settleDeletes(request.rows, async (apiKey) => revokeApiKeyById(apiKey.id));
    }

    const deletedIds = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
    const deletedIdSet = new Set(deletedIds);
    const failedCount = request.rows.length - deletedIds.length;
    const count = request.rows.length;
    const labels =
      request.kind === 'members'
        ? { singular: 'membership', plural: 'memberships', action: 'revoked' }
        : request.kind === 'invitations'
          ? { singular: 'invitation', plural: 'invitations', action: 'canceled' }
          : request.kind === 'roles'
            ? { singular: 'role', plural: 'roles', action: 'deleted' }
            : { singular: 'API key', plural: 'API keys', action: 'revoked' };

    if (deletedIds.length > 0) {
      if (request.kind === 'members') {
        setMembersState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
        adjustMemberExactTotal(-deletedIds.length);
        if (memberPageState.page > 1 && deletedIds.length >= memberVisibleRowCountRef.current) {
          setMemberPage(memberPageState.page - 1);
        }
        if (memberDetailId && deletedIds.includes(memberDetailId)) {
          setMemberDetail({ open: false, loading: false, saving: false, revoking: false, record: null, etag: null, error: null, success: null });
          setSelectedMemberId(null);
        }
      } else if (request.kind === 'invitations') {
        setInvitationsState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
        adjustInvitationExactTotal(-deletedIds.length);
        if (invitationPageState.page > 1 && deletedIds.length >= invitationVisibleRowCountRef.current) {
          setInvitationPage(invitationPageState.page - 1);
        }
        const deletedPendingCount = request.rows.filter(
          (invitation) => invitation.id && deletedIdSet.has(invitation.id) && invitation.status === 'pending'
        ).length;
        setPendingInvitesCount((current) => current == null ? current : Math.max(0, current - deletedPendingCount));
        if (invitationDetailId && deletedIds.includes(invitationDetailId)) {
          setInvitationDetail({ open: false, loading: false, record: null, etag: null, sending: false, canceling: false, error: null, success: null });
          setSelectedInvitationId(null);
        }
        void loadMembers();
      } else if (request.kind === 'roles') {
        setRolesState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
        adjustRoleExactTotal(-deletedIds.length);
        if (rolePageState.page > 1 && deletedIds.length >= roleVisibleRowCountRef.current) {
          setRolePage(rolePageState.page - 1);
        }
        if (roleEditorId && deletedIds.includes(roleEditorId)) {
          setRoleEditor({ mode: 'create', open: false, loading: false, saving: false, deleting: false, record: null, etag: null, error: null, success: null });
          setSelectedRoleId(null);
        }
        void Promise.all([loadMembers(), loadInvitations()]);
      } else {
        const revokedActiveCount = request.rows.filter(
          (apiKey) => apiKey.id && deletedIdSet.has(apiKey.id) && apiKey.status === ApiKeyStatusEnum.Active
        ).length;
        setApiKeysState((current) => ({
          ...current,
          items: current.items.map((item) => item.id && deletedIds.includes(item.id) ? { ...item, status: ApiKeyStatusEnum.Revoked } : item)
        }));
        setActiveApiKeysCount((current) => current == null ? current : Math.max(0, current - revokedActiveCount));
        if (apiKeyFilters.status === ApiKeyStatusEnum.Active) {
          adjustApiKeyExactTotal(-revokedActiveCount);
          if (apiKeyPageState.page > 1 && revokedActiveCount >= apiKeyVisibleRowCountRef.current) {
            setApiKeyPage(apiKeyPageState.page - 1);
          }
        }
        if (apiKeyDetail.record?.id && deletedIds.includes(apiKeyDetail.record.id)) {
          setApiKeyDetail((current) => ({
            ...current,
            revoking: false,
            record: current.record ? { ...current.record, status: ApiKeyStatusEnum.Revoked } : null,
            success: 'API key revoked.'
          }));
        }
      }

      setPendingAccessDelete(null);
      setAccessDeleteToast({
        tone: failedCount > 0 ? 'warning' : 'success',
        message:
          failedCount > 0
            ? `${deletedIds.length} of ${count} ${labels.plural} ${labels.action}. ${failedCount} could not be ${labels.action}.`
            : `${deletedIds.length} ${deletedIds.length === 1 ? labels.singular : labels.plural} ${labels.action} successfully.`
      });
    } else {
      const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      const reason = firstFailure ? await extractErrorMessage(firstFailure.reason) : `Failed to update the selected ${labels.plural}.`;
      setAccessDeleteToast({ tone: 'error', message: reason });
    }
    setAccessDeleteBusy(false);
  }, [
    accessDeleteBusy,
    adjustApiKeyExactTotal,
    adjustInvitationExactTotal,
    adjustMemberExactTotal,
    adjustRoleExactTotal,
    apiKeyDetail.record?.id,
    apiKeyFilters.status,
    apiKeyPageState.page,
    createIfMatchInitOverride,
    fetchRoleDetail,
    invitationDetailId,
    invitationPageState.page,
    invitationsApi,
    loadInvitations,
    loadMembers,
    memberDetailId,
    memberPageState.page,
    membershipsApi,
    orgId,
    pendingAccessDelete,
    resolveInvitationEtag,
    resolveMembershipEtag,
    revokeApiKeyById,
    roleEditorId,
    rolePageState.page,
    rolesApi
  ]);

  const handleIdpBulkDelete = useCallback(
    (rows: IdpConnectionschema[]) => {
      if (!orgId || rows.length === 0 || idpDeleteBusy) {
        return;
      }
      setPendingIdpDeletes(rows);
    },
    [idpDeleteBusy, orgId]
  );

  const confirmIdpBulkDelete = useCallback(
    async () => {
      const rows = pendingIdpDeletes;
      if (!orgId || !rows || rows.length === 0 || idpDeleteBusy) {
        return;
      }
      setIdpDeleteBusy(true);
      const deletedIds: string[] = [];
      let failedCount = 0;
      for (const connection of rows) {
        if (!connection.id) {
          failedCount += 1;
          continue;
        }
        try {
          const response = await idpConnectionsApi.orgsOrgIdIdpConnectionsConnectionIdGetRaw({ orgId, connectionId: connection.id });
          const etag = response.raw.headers.get('ETag') ?? undefined;
          if (!etag) {
            failedCount += 1;
            continue;
          }
          await idpConnectionsApi.orgsOrgIdIdpConnectionsConnectionIdDelete({ orgId, connectionId: connection.id, ifMatch: etag });
          deletedIds.push(connection.id);
          if (idpSelectedId === connection.id) {
            setIdpDrawerOpen(false);
            setIdpSelectedId(null);
            setIdpDrawerInitialTab('overview');
          }
        } catch (error) {
          failedCount += 1;
          console.error('Failed to delete IdP connection.', error);
        }
      }
      if (deletedIds.length > 0) {
        setIdpConnections((current) => current.filter((connection) => !connection.id || !deletedIds.includes(connection.id)));
        setPendingIdpDeletes(null);
        setIdpHighlightRowId(null);
        setIdpDeleteToast({
          tone: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `${deletedIds.length} of ${rows.length} IdP connections deleted. ${failedCount} could not be deleted.`
              : `${deletedIds.length} IdP connection${deletedIds.length === 1 ? '' : 's'} deleted successfully.`
        });
        setIdpDeleteBusy(false);
        await loadIdpConnections();
      } else {
        setIdpDeleteToast({ tone: 'error', message: 'Failed to delete the selected IdP connections.' });
        setIdpDeleteBusy(false);
      }
    },
    [idpConnectionsApi, idpDeleteBusy, idpSelectedId, loadIdpConnections, orgId, pendingIdpDeletes]
  );

  useEffect(() => {
    const intent = searchParams.get('intent');
    if (intent === 'invite') {
      setInviteEditor({ open: true, saving: false, error: null, success: null });
      setActiveTab('invitations');
    } else if (intent === 'new-role') {
      openRoleEditor({} as Role, 'create');
      setActiveTab('roles');
    } else if (intent === 'new-api-key') {
      setApiKeyCreate({ open: true, saving: false, error: null, response: null });
      setActiveTab('api-keys');
    } else if (intent === 'new-oidc-connection') {
      setActiveTab('oidc-connections');
      openIdpWizard('oidc');
    } else if (intent === 'new-saml-connection') {
      setActiveTab('saml-connections');
      openIdpWizard('saml');
    }
  }, [openIdpWizard, openRoleEditor, searchParams]);

  useEffect(() => {
    if (selectedMemberId && !membersState.items.some((member) => member.id === selectedMemberId)) {
      setSelectedMemberId(null);
      setMemberDetail((prev) => ({ ...prev, open: false }));
    }
  }, [membersState.items, selectedMemberId]);

  useEffect(() => {
    if (selectedInvitationId && !invitationsState.items.some((invite) => invite.id === selectedInvitationId)) {
      setSelectedInvitationId(null);
      setInvitationDetail((prev) => ({ ...prev, open: false }));
    }
  }, [invitationsState.items, selectedInvitationId]);

  useEffect(() => {
    if (selectedRoleId && !rolesState.items.some((role) => role.id === selectedRoleId)) {
      setSelectedRoleId(null);
    }
  }, [rolesState.items, selectedRoleId]);

  useEffect(() => {
    if (selectedApiKeyId && !apiKeysState.items.some((item) => item.id === selectedApiKeyId)) {
      setSelectedApiKeyId(null);
      setApiKeyDetail((prev) => ({ ...prev, open: false }));
    }
  }, [apiKeysState.items, selectedApiKeyId]);

  useEffect(() => {
    setRoleUsageMap(Object.fromEntries(rolesState.items.map((role) => [role.id ?? role.slug, role.memberCount ?? 0])));
  }, [rolesState.items]);

  const handleViewMembersWithRole = useCallback(
    (slug: string) => {
      setActiveTab('members');
      updateRoleFilters(() => ({ ...defaultRoleFilters }));
      updateMemberFilters(() => ({ ...defaultMemberFilters, roleSlug: slug }));
    },
    [updateMemberFilters, updateRoleFilters]
  );

  const roleCatalogFetcher = useMemo(() => !orgId ? null : () => collectCursorPages<Role>((after) =>
    rolesApi.orgsOrgIdRolesGet({ orgId, limit: 200, after })), [rolesApi, orgId, roleCatalogVersion]);
  const roleCatalogQuery = useApiQuery(roleCatalogFetcher, [roleCatalogFetcher]);
  const roleCatalog = roleCatalogQuery.data?.items ?? [];

  const sortedRoles = useMemo(() => {
    return [...roleCatalog].sort((a, b) => {
      const aName = a.name ?? a.slug ?? '';
      const bName = b.name ?? b.slug ?? '';
      return aName.localeCompare(bName);
    });
  }, [roleCatalog]);

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Access & Identity"
        description="Manage members, API keys, invitations, and roles across the organization."
        topSlot={<OrgModulePlaceholder intent="Select an organization to manage members." />}
      >
        <OrgModulePlaceholder intent="Memberships, invitations, API keys, and roles will appear once an organization is active." />
      </OrgModuleLayout>
    );
  }

  const intent = searchParams.get('intent');

  const filteredMembers = membersState.items;
  const filteredInvitations = invitationsState.items;
  const filteredRoles = rolesState.items;

  const rolePermissionFilterOptions = useMemo(
    () => uniqueStringValues([...knownPermissionResources, ...rolesState.items.flatMap((role) => collectPermissionResources(role.permissions))]),
    [rolesState.items]
  );

  const filteredApiKeys = useMemo(() => {
    const query = apiKeyFilters.q.trim().toLowerCase();
    return apiKeysState.items.filter((item) => {
      const matchesStatus = apiKeyFilters.status === 'all' || item.status === apiKeyFilters.status;
      const matchesQuery =
        query.length === 0 ||
        (item.name ?? '').toLowerCase().includes(query) ||
        (item.prefix ?? '').toLowerCase().includes(query) ||
        (item.rolesResolved ?? []).some((role) => (role.name ?? role.slug ?? '').toLowerCase().includes(query));
      return matchesStatus && matchesQuery;
    });
  }, [apiKeyFilters, apiKeysState.items]);

  const memberColumns = useMemo<Array<ListWorkspaceColumn<Membership>>>(() => {
    return [
      {
        key: 'name',
        header: 'Name',
        render: (row: Membership) => resolveUserName(row.user, row.userId ?? '—')
      },
      {
        key: 'email',
        header: 'Email',
        render: (row: Membership) => row.user?.email ?? '—'
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (row: Membership) => {
          const rolesForRow = filterKnownRoleSummaries(row.rolesResolved, rolesState.items);
          return rolesForRow.length > 0 ? (
            <div className="people-chip-group">
              {rolesForRow.map((role) => (
                <span key={role.id ?? role.slug} className="people-chip people-chip--role">
                  {role.name ?? role.slug}
                </span>
              ))}
            </div>
          ) : (
            <span className="people-chip people-chip--empty">None</span>
          );
        }
      },
      {
        key: 'labels',
        header: 'Labels',
        render: (row: Membership) => (
          <div className="people-chip-group">
            {Object.entries(row.labels ?? {}).map(([key, value]) => (
              <span key={`${key}:${value}`} className="people-chip people-chip--label">
                {key}: {value}
              </span>
            ))}
            {Object.keys(row.labels ?? {}).length === 0 && <span className="people-chip people-chip--empty">—</span>}
          </div>
        )
      },
      {
        key: 'status',
        header: 'Status',
        render: (row: Membership) => <StatusBadge status={row.status} />
      },
      {
        key: 'activity',
        header: 'Last activity',
        render: (row: Membership) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      }
    ];
  }, [rolesState.items]);

  const invitationColumns = useMemo<Array<ListWorkspaceColumn<Invitation>>>(() => {
    return [
      { key: 'email', header: 'Email', render: (row: Invitation) => row.email },
      {
        key: 'roles',
        header: 'Roles',
        render: (row: Invitation) =>
          row.rolesResolved && row.rolesResolved.length > 0 ? (
            <div className="people-chip-group">
              {row.rolesResolved.map((role) => (
                <span key={role.id ?? role.slug} className="people-chip people-chip--role">
                  {role.name ?? role.slug}
                </span>
              ))}
            </div>
          ) : (
            <span className="people-chip people-chip--empty">None</span>
          )
      },
      { key: 'status', header: 'Status', render: (row: Invitation) => <InvitationStatusBadge status={row.status} /> },
      { key: 'invitedAt', header: 'Invited', render: (row: Invitation) => formatDateTime(row.createdAt ?? null) },
      { key: 'expiresAt', header: 'Expires', render: (row: Invitation) => formatDateTime(row.expiresAt ?? null) }
    ];
  }, []);

  const roleColumns = useMemo<Array<ListWorkspaceColumn<Role>>>(() => {
    return [
      { key: 'name', header: 'Role', render: (row: Role) => row.name ?? row.slug },
      { key: 'slug', header: 'Slug', render: (row: Role) => row.slug },
      { key: 'scope', header: 'Scope', render: (row: Role) => row.scope },
      { key: 'status', header: 'Status', render: (row: Role) => <RoleStatusBadge status={row.status} /> },
      {
        key: 'usage',
        header: 'Members',
        render: (row: Role) => {
          const usage = row.id ? roleUsageMap[row.id] ?? 0 : 0;
          if (!row.slug || usage === 0) {
            return formatNumber(usage);
          }
          return (
            <button
              type="button"
              className="link-button"
              onClick={(event) => {
                event.stopPropagation();
                handleViewMembersWithRole(row.slug ?? '');
              }}
            >
              {formatNumber(usage)}
            </button>
          );
        }
      },
      {
        key: 'permissions',
        header: 'Permissions',
        render: (row: Role) =>
          row.permissions.length ? (
            <div className="people-chip-group">
              {row.permissions.map((perm, index) => (
                <span key={`${perm.resource}-${index}`} className="people-chip people-chip--permission">
                  {normalizePermissionResource(perm.resource)}:{normalizePermissionVerbs(perm.verbs).join('/')}
                </span>
              ))}
            </div>
          ) : (
            <span className="people-chip people-chip--empty">None</span>
          )
      }
    ];
  }, [handleViewMembersWithRole, roleUsageMap]);

  const apiKeyColumns = useMemo<Array<ListWorkspaceColumn<ApiKey>>>(() => {
    return [
      { key: 'name', header: 'Name', render: (row: ApiKey) => row.name ?? '—' },
      {
        key: 'roles',
        header: 'Roles',
        render: (row: ApiKey) =>
          row.rolesResolved && row.rolesResolved.length > 0 ? (
            <div className="people-chip-group">
              {row.rolesResolved.map((role) => (
                <span key={role.id ?? role.slug} className="people-chip people-chip--role">
                  {role.name ?? role.slug}
                </span>
              ))}
            </div>
          ) : (
            <span className="people-chip people-chip--empty">None</span>
          )
      },
      { key: 'status', header: 'Status', render: (row: ApiKey) => <ApiKeyStatusBadge status={row.status as ApiKeyStatusValue} /> },
      {
        key: 'identifier',
        header: 'Identifier',
        render: (row: ApiKey) => {
          if (row.prefix) {
            return `${row.prefix}${row.last4 ?? ''}`;
          }
          return row.last4 ?? '—';
        }
      },
      { key: 'lastUsed', header: 'Last used', render: (row: ApiKey) => formatDateTime(row.lastUsedAt ?? null) },
      { key: 'expiresAt', header: 'Expires', render: (row: ApiKey) => formatDateTime(row.expiresAt ?? null) }
    ];
  }, []);

  const memberFilterBar = (
    <div className="list-workspace__filter-grid">
      <label>
        Search members
        <input
          type="search"
          placeholder="Name, email, or ID"
          value={memberFilters.q}
          onChange={(event) => updateMemberFilters((prev) => ({ ...prev, q: event.target.value }))}
        />
      </label>
      <label>
        Status
        <select value={memberFilters.status} onChange={(event) => updateMemberFilters((prev) => ({ ...prev, status: event.target.value as MemberFilters['status'] }))}>
          <option value="all">All statuses</option>
          {membershipStatusOrder.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <label>
        Role slug
        <input
          type="text"
          placeholder="e.g. admin"
          value={memberFilters.roleSlug === 'all' ? '' : memberFilters.roleSlug}
          onChange={(event) =>
            updateMemberFilters((prev) => ({
              ...prev,
              roleSlug: event.target.value.trim() ? event.target.value.trim() : 'all'
            }))
          }
        />
      </label>
      <label>
        Label (key:value)
        <input
          type="text"
          placeholder="tier:gold"
          value={memberFilters.label}
          onChange={(event) => updateMemberFilters((prev) => ({ ...prev, label: event.target.value }))}
        />
      </label>
    </div>
  );

  const invitationFilterBar = (
    <div className="list-workspace__filter-grid">
      <label>
        Status
        <select
          value={invitationFilters.status}
          onChange={(event) => updateInvitationFilters((prev) => ({ ...prev, status: event.target.value as InvitationFilters['status'] }))}
        >
          <option value="all">All statuses</option>
          {invitationStatusOrder.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <label>
        Search email
        <input type="search" value={invitationFilters.q} onChange={(event) => updateInvitationFilters((prev) => ({ ...prev, q: event.target.value }))} />
      </label>
    </div>
  );

  const roleFilterBar = (
    <div className="list-workspace__filter-grid">
      <label>
        Search name or slug
        <input type="search" value={roleFilters.q} onChange={(event) => updateRoleFilters((prev) => ({ ...prev, q: event.target.value }))} />
      </label>
      <label>
        Permission resource
        <select
          value={roleFilters.permission}
          onChange={(event) => updateRoleFilters((prev) => ({ ...prev, permission: event.target.value as RoleFilters['permission'] }))}
        >
          <option value="all">All permissions</option>
          {rolePermissionFilterOptions.map((resource) => (
            <option key={resource} value={resource}>
              {resource}
            </option>
          ))}
        </select>
      </label>
      <label>
        Usage
        <select value={roleFilters.usage} onChange={(event) => updateRoleFilters((prev) => ({ ...prev, usage: event.target.value as RoleFilters['usage'] }))}>
          <option value="all">All usage</option>
          <option value="with-members">Assigned to members</option>
          <option value="unused">Unused roles</option>
        </select>
      </label>
    </div>
  );

  const apiKeyFilterBar = (
    <div className="list-workspace__filter-grid">
      <label>
        Search name or role
        <input type="search" value={apiKeyFilters.q} onChange={(event) => updateApiKeyFilters((prev) => ({ ...prev, q: event.target.value }))} />
      </label>
      <label>
        Status
        <select value={apiKeyFilters.status} onChange={(event) => updateApiKeyFilters((prev) => ({ ...prev, status: event.target.value as ApiKeyFilters['status'] }))}>
          <option value="all">All statuses</option>
          <option value={ApiKeyStatusEnum.Active}>Active</option>
          <option value={ApiKeyStatusEnum.Revoked}>Revoked</option>
        </select>
      </label>
    </div>
  );

  const membersUnavailable = membersState.backendMissing || Boolean(membersState.error);
  const invitationsUnavailable = invitationsState.backendMissing || Boolean(invitationsState.error);
  const rolesUnavailable = rolesState.backendMissing || Boolean(rolesState.error);
  const apiKeysUnavailable = apiKeysState.backendMissing || Boolean(apiKeysState.error);

  const memberRows = membersUnavailable ? [] : membersState.loading ? [] : filteredMembers;
  const invitationRows = invitationsUnavailable ? [] : invitationsState.loading ? [] : filteredInvitations;
  const roleRows = rolesUnavailable ? [] : rolesState.loading ? [] : filteredRoles;
  const apiKeyRows = apiKeysUnavailable ? [] : apiKeysState.loading ? [] : filteredApiKeys;
  memberVisibleRowCountRef.current = memberRows.length;
  invitationVisibleRowCountRef.current = invitationRows.length;
  roleVisibleRowCountRef.current = roleRows.length;
  apiKeyVisibleRowCountRef.current = apiKeyRows.length;
  const memberPaginationInfo = useMemo(() => deriveMemberPaginationMeta(memberRows.length), [deriveMemberPaginationMeta, memberRows.length]);
  const invitationPaginationInfo = useMemo(
    () => deriveInvitationPaginationMeta(invitationRows.length),
    [deriveInvitationPaginationMeta, invitationRows.length]
  );
  const rolePaginationInfo = useMemo(() => deriveRolePaginationMeta(roleRows.length), [deriveRolePaginationMeta, roleRows.length]);
  const apiKeyPaginationInfo = useMemo(() => deriveApiKeyPaginationMeta(apiKeyRows.length), [deriveApiKeyPaginationMeta, apiKeyRows.length]);

  const membersCount = memberPaginationInfo.exactTotal ?? memberPaginationInfo.totalCount;
  const rolesCount = rolePaginationInfo.exactTotal ?? rolePaginationInfo.totalCount;
  const invitesTotalCount = invitationPaginationInfo.exactTotal ?? invitationPaginationInfo.totalCount;
  const apiKeysCount = apiKeyPaginationInfo.exactTotal ?? apiKeyPaginationInfo.totalCount;
  const pendingInvitesDisplay = pendingInvitesCount ?? (invitationFilters.status === 'pending' ? invitesTotalCount : null);
  const activeApiKeysDisplay =
    activeApiKeysCount ?? (apiKeyFilters.status === ApiKeyStatusEnum.Active ? apiKeysCount : null);

  const memberEmptyState = membersState.backendMissing ? (
    <OrgModulePlaceholder intent="Implement the memberships API to populate this view." />
  ) : membersState.error ? (
    <OrgModulePlaceholder intent={`Failed to load members: ${membersState.error}`} />
  ) : membersState.loading ? (
    <p>Loading members…</p>
  ) : (
    <p>No members match the current filters.</p>
  );

  const invitationEmptyState = invitationsState.backendMissing ? (
    <OrgModulePlaceholder intent="Implement the invitations API to populate this view." />
  ) : invitationsState.error ? (
    <OrgModulePlaceholder intent={`Failed to load invitations: ${invitationsState.error}`} />
  ) : invitationsState.loading ? (
    <p>Loading invitations…</p>
  ) : (
    <p>No invitations match the current filters.</p>
  );

  const roleEmptyState = rolesState.backendMissing ? (
    <OrgModulePlaceholder intent="Implement the roles API to populate this view." />
  ) : rolesState.error ? (
    <OrgModulePlaceholder intent={`Failed to load roles: ${rolesState.error}`} />
  ) : rolesState.loading ? (
    <p>Loading roles…</p>
  ) : (
    <p>No roles match the current filters.</p>
  );

  const apiKeyEmptyState = apiKeysState.backendMissing ? (
    <OrgModulePlaceholder intent="Implement the API keys API to populate this view." />
  ) : apiKeysState.error ? (
    <OrgModulePlaceholder intent={`Failed to load API keys: ${apiKeysState.error}`} />
  ) : apiKeysState.loading ? (
    <p>Loading API keys…</p>
  ) : (
    <p>No API keys match the current filters.</p>
  );

  const oidcConnections = useMemo(() => idpConnections.filter((connection) => connection.type === 'oidc'), [idpConnections]);
  const samlConnections = useMemo(() => idpConnections.filter((connection) => connection.type === 'saml'), [idpConnections]);

  const idpColumns = useMemo<Array<ListWorkspaceColumn<IdpConnectionschema>>>(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (row) => (
          <div>
            <strong>{row.name ?? 'Untitled connection'}</strong>
            {row.description && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{row.description}</div>}
          </div>
        )
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          const badge = formatConnectionStatusBadge(row.status ?? null);
          return <span className={badge.className}>{badge.label}</span>;
        }
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      },
      {
        key: 'source',
        header: 'Source',
        render: (row) => {
          const badge = formatConnectionSourceBadge(deriveConnectionSource(row));
          return <span className={badge.className}>{badge.label}</span>;
        }
      }
    ],
    []
  );

  const buildStaticPagination = useCallback((count: number): ListWorkspacePaginationMeta => {
    return {
      totalCount: count,
      exactTotal: count,
      pageCount: 1,
      hasNextPage: false,
      hasPrevPage: false
    };
  }, []);

  const renderIdpEmptyState = useCallback(
    (type: 'oidc' | 'saml') => {
      if (idpBackendMissing) {
        return <OrgModulePlaceholder intent="Implement the IdP connections API to populate this view." />;
      }
      if (idpError) {
        return <OrgModulePlaceholder intent={`Failed to load IdP connections: ${idpError}`} />;
      }
      if (idpLoading) {
        return <p>Loading IdP connections…</p>;
      }
      return <p>No {type.toUpperCase()} connections configured yet.</p>;
    },
    [idpBackendMissing, idpError, idpLoading]
  );

  const tabItems = useMemo(
    () => [
      { id: 'members', label: 'Members' },
      { id: 'invitations', label: 'Invitations' },
      { id: 'roles', label: 'Roles' },
      { id: 'api-keys', label: 'API Keys' },
      { id: 'oidc-connections', label: 'OIDC Connections' },
      { id: 'saml-connections', label: 'SAML Connections' }
    ],
    []
  );

  const buildIdpWorkspace = useCallback(
    (type: 'oidc' | 'saml', rows: IdpConnectionschema[]): ActiveWorkspaceProps => ({
      rows,
      columns: idpColumns,
      rowId: (row, index) => row.id ?? index,
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      paginationMeta: buildStaticPagination(rows.length),
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      loading: idpLoading,
      emptyState: renderIdpEmptyState(type),
      headerLabel: type === 'oidc' ? 'OIDC Connections' : 'SAML Connections',
      selectionDisabled: false,
      onDeleteSelected: (selectedRows) => handleIdpBulkDelete(selectedRows as IdpConnectionschema[]),
      highlightRowId: idpHighlightRowId,
      onRowClick: openIdpDrawer,
      onAdd: () => openIdpWizard(type),
      onRefresh: loadIdpConnections,
      rowActions: renderIdpRowActions
    }),
    [
      buildStaticPagination,
      handleIdpBulkDelete,
      idpColumns,
      idpHighlightRowId,
      idpLoading,
      loadIdpConnections,
      openIdpDrawer,
      openIdpWizard,
      renderIdpEmptyState,
      renderIdpRowActions
    ]
  );

  const workspaceMap: Record<TabId, ActiveWorkspaceProps> = {
    members: {
      rows: memberRows,
      columns: memberColumns,
      rowId: (row, index) => row.id ?? row.userId ?? index,
      page: memberPageState.page,
      pageSize: memberPageState.pageSize,
      paginationMeta: memberPaginationInfo,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageChange: (next) => setMemberPage(next),
      onPageSizeChange: (next) => setMemberPageSize(next),
      loading: membersUnavailable ? false : membersState.loading,
      emptyState: memberEmptyState,
      headerLabel: 'Members',
      filterBar: memberFilterBar,
      filterActive: memberFiltersActive,
      onFilterReset: () => updateMemberFilters(() => ({ ...defaultMemberFilters })),
      onRowClick: handleMemberSelect,
      highlightRowId: selectedMemberId,
      selectionDisabled: false,
      onDeleteSelected: (rows) => handleMembersBulkRevoke(rows as Membership[]),
      onAdd: handleInviteDrawerOpen,
      onRefresh: loadMembers,
      rowActions: (row: Membership) => {
        const disabled = memberActionBusy === row.id;
        return (
          <button
            type="button"
            disabled={disabled || !row.id}
            onClick={(event) => {
              event.stopPropagation();
              handleMemberStatusQuick(row, row.status === 'active' ? 'suspended' : 'active');
            }}
          >
            {row.status === 'active' ? 'Suspend' : 'Activate'}
          </button>
        );
      }
    },
    invitations: {
      rows: invitationRows,
      columns: invitationColumns,
      rowId: (row, index) => row.id ?? index,
      page: invitationPageState.page,
      pageSize: invitationPageState.pageSize,
      paginationMeta: invitationPaginationInfo,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageChange: (next) => setInvitationPage(next),
      onPageSizeChange: (next) => setInvitationPageSize(next),
      loading: invitationsUnavailable ? false : invitationsState.loading,
      emptyState: invitationEmptyState,
      headerLabel: 'Invitations',
      filterBar: invitationFilterBar,
      filterActive: invitationFiltersActive,
      onFilterReset: () => updateInvitationFilters(() => ({ ...defaultInvitationFilters })),
      onRowClick: handleInvitationSelect,
      highlightRowId: selectedInvitationId,
      selectionDisabled: false,
      onDeleteSelected: (rows) => handleInvitationsBulkCancel(rows as Invitation[]),
      onAdd: handleInviteDrawerOpen,
      onRefresh: loadInvitations,
      rowActions: (row: Invitation) => {
        const disabled = invitationActionBusy === row.id;
        return (
          <>
            <button
              type="button"
              disabled={disabled || !row.id}
              onClick={(event) => {
                event.stopPropagation();
                handleInvitationResendQuick(row);
              }}
            >
              Resend
            </button>
            <button
              type="button"
              disabled={disabled || !row.id}
              onClick={(event) => {
                event.stopPropagation();
                handleInvitationCancelQuick(row);
              }}
            >
              Cancel
            </button>
          </>
        );
      }
    },
    roles: {
      rows: roleRows,
      columns: roleColumns,
      rowId: (row, index) => row.id ?? index,
      page: rolePageState.page,
      pageSize: rolePageState.pageSize,
      paginationMeta: rolePaginationInfo,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageChange: (next) => setRolePage(next),
      onPageSizeChange: (next) => setRolePageSize(next),
      loading: rolesUnavailable ? false : rolesState.loading,
      emptyState: roleEmptyState,
      headerLabel: 'Roles',
      filterBar: roleFilterBar,
      filterActive: roleFiltersActive,
      onFilterReset: () => updateRoleFilters(() => ({ ...defaultRoleFilters })),
      onRowClick: handleRoleSelect,
      highlightRowId: selectedRoleId,
      selectionDisabled: false,
      onDeleteSelected: (rows) => handleRolesBulkDelete(rows as Role[]),
      onAdd: handleRoleCreate,
      onRefresh: loadRoles
    },
    'api-keys': {
      rows: apiKeyRows,
      columns: apiKeyColumns,
      rowId: (row, index) => row.id ?? `${row.prefix ?? 'api-key'}-${index}`,
      page: apiKeyPageState.page,
      pageSize: apiKeyPageState.pageSize,
      paginationMeta: apiKeyPaginationInfo,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageChange: (next) => setApiKeyPage(next),
      onPageSizeChange: (next) => setApiKeyPageSize(next),
      loading: apiKeysUnavailable ? false : apiKeysState.loading,
      emptyState: apiKeyEmptyState,
      headerLabel: 'API keys',
      filterBar: apiKeyFilterBar,
      filterActive: apiKeyFiltersActive,
      onFilterReset: () => updateApiKeyFilters(() => ({ ...defaultApiKeyFilters })),
      onRowClick: handleApiKeySelect,
      highlightRowId: selectedApiKeyId,
      selectionDisabled: false,
      onDeleteSelected: (rows) => handleApiKeysBulkRevoke(rows as ApiKey[]),
      onAdd: handleApiKeyCreateOpen,
      onRefresh: loadApiKeys,
      rowActions: (row: ApiKey) => {
        const disabled = apiKeyActionBusy === row.id;
        const isActive = row.status === ApiKeyStatusEnum.Active;
        return (
          <button
            type="button"
            disabled={disabled || !row.id || !isActive}
            onClick={(event) => {
              event.stopPropagation();
              handleApiKeyRevokeQuick(row);
            }}
          >
            {isActive ? 'Revoke' : 'Revoked'}
          </button>
        );
      }
    },
    'oidc-connections': {
      ...buildIdpWorkspace('oidc', oidcConnections)
    },
    'saml-connections': {
      ...buildIdpWorkspace('saml', samlConnections)
    }
  };

  const activeWorkspace = workspaceMap[activeTab];
  const accessDeleteCount = pendingAccessDelete?.rows.length ?? 0;
  const accessDeleteCopy =
    pendingAccessDelete?.kind === 'members'
      ? {
          title: `Revoke ${accessDeleteCount} membership${accessDeleteCount === 1 ? '' : 's'}?`,
          description: 'This removes the selected members from the organization.',
          confirmLabel: 'Revoke',
          deletingLabel: 'Revoking...'
        }
      : pendingAccessDelete?.kind === 'invitations'
        ? {
            title: `Cancel ${accessDeleteCount} invitation${accessDeleteCount === 1 ? '' : 's'}?`,
            description: 'The selected invitation links will no longer be usable.',
            confirmLabel: 'Cancel invitation',
            deletingLabel: 'Canceling...'
          }
        : pendingAccessDelete?.kind === 'roles'
          ? {
              title: `Delete ${accessDeleteCount} role${accessDeleteCount === 1 ? '' : 's'}?`,
              description: 'This permanently removes the selected roles.',
              confirmLabel: 'Delete',
              deletingLabel: 'Deleting...'
            }
          : {
              title: `Revoke ${accessDeleteCount} API key${accessDeleteCount === 1 ? '' : 's'}?`,
              description: 'The selected API keys will stop authenticating immediately.',
              confirmLabel: 'Revoke',
              deletingLabel: 'Revoking...'
            };

  return (
    <OrgModuleLayout
      title="Access & Identity"
      description="Manage members, API keys, invitations, and roles across the organization."
      topSlot={
        <div className="people-access__top">
          <div className="kpi-grid">
            <KpiCard label="Members" value={membersState.loading ? '…' : formatNumber(membersCount)} />
            <KpiCard label="Roles" value={rolesState.loading ? '…' : formatNumber(rolesCount)} />
            <KpiCard
              label="API Keys"
              value={activeApiKeysDisplay == null ? '…' : formatNumber(activeApiKeysDisplay)}
              secondary={apiKeysCount ? `${formatNumber(apiKeysCount)} total` : undefined}
            />
            <KpiCard
              label="Pending Invites"
              value={pendingInvitesDisplay == null ? '…' : formatNumber(pendingInvitesDisplay)}
              secondary={invitesTotalCount ? `${formatNumber(invitesTotalCount)} total invites` : undefined}
            />
          </div>
          <div className="shortcut-bar">
            <ShortcutButton
              label="Invite Member"
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('intent', 'invite');
                  return next;
                });
              }}
            />
            <ShortcutButton
              label="Create Role"
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('intent', 'new-role');
                  return next;
                });
              }}
            />
            <ShortcutButton
              label="Create API Key"
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('intent', 'new-api-key');
                  return next;
                });
              }}
            />
            <ShortcutButton label="Create OIDC Connection" onClick={() => openIdpWizard('oidc')} />
            <ShortcutButton label="Create SAML Connection" onClick={() => openIdpWizard('saml')} />
          </div>
        </div>
      }
    >
      <div className="people-access">
        {alerts.length > 0 && <AlertStack alerts={alerts} onDismiss={dismissAlert} />}
        <ListWorkspace
          tabs={tabItems}
          activeTabId={activeTab}
          onTabChange={(id) => setActiveTab(id as TabId)}
          {...activeWorkspace}
        />
      </div>

      <MemberDrawer
        open={memberDetail.open}
        loading={memberDetail.loading}
        saving={memberDetail.saving}
        revoking={memberDetail.revoking}
        record={memberDetail.record}
        etag={memberDetail.etag}
        error={memberDetail.error}
        success={memberDetail.success}
        roles={sortedRoles}
        onClose={() => setMemberDetail((prev) => ({ ...prev, open: false }))}
        onSubmit={handleMemberUpdate}
        onRevoke={handleMemberRevoke}
      />

      <InvitationDrawer
        open={invitationDetail.open}
        loading={invitationDetail.loading}
        record={invitationDetail.record}
        sending={invitationDetail.sending}
        canceling={invitationDetail.canceling}
        error={invitationDetail.error}
        success={invitationDetail.success}
        onClose={() => setInvitationDetail((prev) => ({ ...prev, open: false }))}
        onResend={handleInvitationResend}
        onCancel={handleInvitationCancel}
      />

      <InviteMemberDrawer
        open={inviteEditor.open}
        saving={inviteEditor.saving}
        error={inviteEditor.error}
        roles={sortedRoles}
        onClose={() => {
          setInviteEditor({ open: false, saving: false, error: null, success: null });
          if (intent === 'invite') {
            clearIntent();
          }
        }}
        onSubmit={handleInviteSubmit}
      />

      <RoleDrawer
        open={roleEditor.open}
        mode={roleEditor.mode}
        loading={roleEditor.loading}
        saving={roleEditor.saving}
        deleting={roleEditor.deleting}
        record={roleEditor.record}
        etag={roleEditor.etag}
        error={roleEditor.error}
        success={roleEditor.success}
        usage={roleEditor.record?.id ? roleUsageMap[roleEditor.record.id] ?? 0 : 0}
        onClose={() => {
          setRoleEditor((prev) => ({ ...prev, open: false }));
          if (intent === 'new-role') {
            clearIntent();
          }
        }}
        onSave={handleRoleSave}
        onDelete={roleEditor.mode === 'edit' ? handleRoleDelete : undefined}
        onViewMembers={handleViewMembersWithRole}
      />
      <ApiKeyDrawer
        open={apiKeyDetail.open}
        loading={apiKeyDetail.loading}
        saving={apiKeyDetail.saving}
        revoking={apiKeyDetail.revoking}
        record={apiKeyDetail.record}
        etag={apiKeyDetail.etag}
        error={apiKeyDetail.error}
        success={apiKeyDetail.success}
        roles={roleCatalog}
        onClose={() => setApiKeyDetail((prev) => ({ ...prev, open: false }))}
        onSubmit={handleApiKeyUpdate}
        onRevoke={handleApiKeyRevoke}
      />
      <CreateApiKeyDrawer
        open={apiKeyCreate.open}
        saving={apiKeyCreate.saving}
        error={apiKeyCreate.error}
        response={apiKeyCreate.response}
        roles={roleCatalog}
        onClose={() => {
          setApiKeyCreate({ open: false, saving: false, error: null, response: null });
          if (intent === 'new-api-key') {
            clearIntent();
          }
        }}
        onSubmit={handleApiKeyCreate}
      />

      <ConnectionWizard
        orgId={orgId ?? ''}
        open={idpWizardOpen && Boolean(orgId)}
        preferredType={idpPreferredType}
        onClose={closeIdpWizard}
        onCreated={handleIdpCreated}
      />

      <ConnectionDrawer
        orgId={orgId ?? ''}
        connectionId={idpSelectedId}
        open={idpDrawerOpen && Boolean(orgId) && idpSelectedId != null}
        onClose={() => {
          setIdpDrawerOpen(false);
          setIdpDrawerInitialTab('overview');
        }}
        onUpdated={handleIdpUpdated}
        onDeleted={handleIdpDeleted}
        onDeleteFeedback={setIdpDeleteToast}
        initialTab={idpDrawerInitialTab}
      />
      <DeleteConfirmationModal
        open={pendingAccessDelete != null}
        title={accessDeleteCopy.title}
        description={accessDeleteCopy.description}
        deleting={accessDeleteBusy}
        confirmLabel={accessDeleteCopy.confirmLabel}
        deletingLabel={accessDeleteCopy.deletingLabel}
        onCancel={() => setPendingAccessDelete(null)}
        onConfirm={() => void confirmAccessDelete()}
      />
      {accessDeleteToast && (
        <StatusToast
          tone={accessDeleteToast.tone}
          message={accessDeleteToast.message}
          occurrence={accessDeleteToast}
          onDismiss={() => setAccessDeleteToast(null)}
        />
      )}
      <DeleteConfirmationModal
        open={pendingIdpDeletes != null}
        title={`Delete ${pendingIdpDeletes?.length ?? 0} IdP connection${pendingIdpDeletes?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected connection configuration."
        deleting={idpDeleteBusy}
        onCancel={() => setPendingIdpDeletes(null)}
        onConfirm={() => void confirmIdpBulkDelete()}
      />
      {idpDeleteToast && <StatusToast tone={idpDeleteToast.tone} message={idpDeleteToast.message} occurrence={idpDeleteToast} onDismiss={() => setIdpDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

export default AccessIdentityPage;

interface AlertStackProps {
  alerts: AlertMessage[];
  onDismiss: (id: string) => void;
}

const AlertStack = ({ alerts, onDismiss }: AlertStackProps) => {
  return (
    <div className="people-alerts">
      {alerts.map((alert) => (
        <div key={alert.id} className={`people-alert people-alert--${alert.tone}`} role="status">
          <div>
            {alert.title && <p className="people-alert__title">{alert.title}</p>}
            <p className="people-alert__message">{alert.message}</p>
          </div>
          <button type="button" className="people-alert__close" onClick={() => onDismiss(alert.id)} aria-label="Dismiss message">
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

interface StatusBadgeProps {
  status: MembershipStatusValue;
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const tone = status === 'active' ? 'success' : 'warning';
  return <span className={`people-badge people-badge--${tone}`}>{status}</span>;
};

interface InvitationStatusBadgeProps {
  status: InvitationStatusValue;
}

const InvitationStatusBadge = ({ status }: InvitationStatusBadgeProps) => {
  const tone =
    status === 'pending'
      ? 'info'
      : status === 'accepted'
      ? 'success'
      : status === 'revoked'
      ? 'danger'
      : 'warning';
  return <span className={`people-badge people-badge--${tone}`}>{status}</span>;
};

interface RoleStatusBadgeProps {
  status: RoleStatusValue;
}

const RoleStatusBadge = ({ status }: RoleStatusBadgeProps) => {
  const tone = status === 'active' ? 'success' : 'warning';
  return <span className={`people-badge people-badge--${tone}`}>{status}</span>;
};

interface MemberDrawerProps {
  open: boolean;
  loading: boolean;
  saving: boolean;
  revoking: boolean;
  record: Membership | null;
  etag: string | null;
  error: string | null;
  success: string | null;
  roles: Role[];
  onClose: () => void;
  onSubmit: (update: MembershipUpdate) => void;
  onRevoke: () => void;
}

const MemberDrawer = ({ open, loading, saving, revoking, record, etag, error, success, roles, onClose, onSubmit, onRevoke }: MemberDrawerProps) => {
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [status, setStatus] = useState<MembershipStatusValue>('active');
  const [labelPairs, setLabelPairs] = useState<LabelPair[]>([]);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (!record) {
      return;
    }
    setRoleIds(deriveMembershipRoleIds(record, roles));
    setStatus(record.status ?? 'active');
    setLabelPairs(toLabelPairs(record.labels));
  }, [record, etag, roles]);

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    setRoleIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, roleId]));
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const handleLabelChange = (id: string, field: 'key' | 'value', value: string) => {
    setLabelPairs((prev) => prev.map((pair) => (pair.id === id ? { ...pair, [field]: value } : pair)));
  };

  const handleLabelAdd = () => {
    setLabelPairs((prev) => [...prev, { id: randomId(), key: '', value: '' }]);
  };

  const handleLabelRemove = (id: string) => {
    setLabelPairs((prev) => prev.filter((pair) => pair.id !== id));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextStatus: NonNullable<MembershipUpdate['status']> = status === 'active' ? 'active' : 'suspended';
    onSubmit({
      roleIds,
      status: nextStatus,
      labels: labelPairsToRecord(labelPairs)
    });
  };

  const drawerActions = (
    <button
      type="button"
      className="drawer-icon-button"
      aria-label="Save membership"
      title="Save membership"
      onClick={() => formRef.current?.requestSubmit()}
      disabled={saving || revoking}
    >
      <SaveIcon size={18} />
    </button>
  );

  return (
    <Drawer
      open={open}
      title={resolveUserName(record?.user, 'Member')}
      subtitle={resolveUserEmail(record?.user) ?? undefined}
      onClose={onClose}
      width="wide"
      actions={drawerActions}
    >
      {loading && <p>Loading membership…</p>}
      {!loading && !record && <p className="people-empty">Unable to load membership details.</p>}
      {!loading && record && (
        <form className="people-drawer" onSubmit={handleSubmit} ref={formRef}>
          {error && <div className="people-alert people-alert--danger">{error}</div>}
          {success && <div className="people-alert people-alert--success">{success}</div>}
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Name</span>
                <strong>{resolveUserName(record.user, 'Member')}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{resolveUserEmail(record.user) ?? '—'}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{record.status ?? '—'}</strong>
              </div>
              <div>
                <span>Joined</span>
                <strong>{formatDateTime(record.createdAt ?? null)}</strong>
              </div>
            </div>
          </div>

          <section className="people-drawer__section">
            <header>
              <h3>Membership status</h3>
              <p>Control whether the member can access the organization.</p>
            </header>
            <select value={status} onChange={(event) => setStatus(event.target.value as MembershipStatusValue)} disabled={saving || revoking}>
              {membershipStatusOrder.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </section>

          <section className="people-drawer__section">
            <header>
              <h3>Roles</h3>
              <p>Assign roles to control permissions across the console.</p>
            </header>
            <RoleSelector roles={roles} selected={roleIds} onChange={setRoleIds} onToggle={handleRoleToggle} disabled={saving || revoking} />
          </section>

          <section className="people-drawer__section">
            <header>
              <h3>Labels</h3>
              <p>Track cohorts like support tiers or regions. Labels are arbitrary key/value pairs.</p>
            </header>
            <div className="people-labels">
              {labelPairs.map((pair) => (
                <div key={pair.id} className="people-labels__row">
                  <input
                    type="text"
                    placeholder="Key"
                    value={pair.key}
                    onChange={(event) => handleLabelChange(pair.id, 'key', event.target.value)}
                    disabled={saving || revoking}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={pair.value}
                    onChange={(event) => handleLabelChange(pair.id, 'value', event.target.value)}
                    disabled={saving || revoking}
                  />
                  <button type="button" className="people-labels__remove" onClick={() => handleLabelRemove(pair.id)} disabled={saving || revoking}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="ui-button ui-button--ghost" onClick={handleLabelAdd} disabled={saving || revoking}>
                Add label
              </button>
            </div>
          </section>

          <footer className="people-drawer__footer">
            <div className="people-drawer__danger">
              <button type="button" className="ui-button ui-button--danger" onClick={onRevoke} disabled={saving || revoking}>
                {revoking ? 'Revoking…' : 'Revoke membership'}
              </button>
            </div>
          </footer>
        </form>
      )}
    </Drawer>
  );
};

interface InvitationDrawerProps {
  open: boolean;
  loading: boolean;
  record: Invitation | null;
  sending: boolean;
  canceling: boolean;
  error: string | null;
  success: string | null;
  onClose: () => void;
  onResend: () => void;
  onCancel: () => void;
}

const InvitationDrawer = ({ open, loading, record, sending, canceling, error, success, onClose, onResend, onCancel }: InvitationDrawerProps) => {
  return (
    <Drawer
      open={open}
      title={record?.email ?? 'Invitation'}
      subtitle={record ? `Status: ${record.status}` : undefined}
      onClose={onClose}
    >
      {loading && <p>Loading invitation…</p>}
      {!loading && !record && <p className="people-empty">Unable to load invitation.</p>}
      {!loading && record && (
        <div className="people-drawer">
          {error && <div className="people-alert people-alert--danger">{error}</div>}
          {success && <div className="people-alert people-alert--success">{success}</div>}
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Email</span>
                <strong>{record.email}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{record.status}</strong>
              </div>
              <div>
                <span>Invited</span>
                <strong>{formatDateTime(record.createdAt ?? null)}</strong>
              </div>
              <div>
                <span>Expires</span>
                <strong>{formatDateTime(record.expiresAt ?? null)}</strong>
              </div>
            </div>
          </div>

          <section className="people-drawer__section">
            <header>
              <h3>Roles</h3>
            </header>
            <div className="people-chip-group">
              {record.rolesResolved.map((role) => (
                <span key={role.id ?? role.slug} className="people-chip people-chip--role">
                  {role.name ?? role.slug}
                </span>
              ))}
              {record.rolesResolved.length === 0 && <span className="people-chip people-chip--empty">None</span>}
            </div>
          </section>

          <footer className="people-drawer__footer">
            <div className="people-drawer__actions">
              <button type="button" className="ui-button" onClick={onResend} disabled={sending || canceling}>
                {sending ? 'Resending…' : 'Resend'}
              </button>
              <button type="button" className="ui-button ui-button--danger" onClick={onCancel} disabled={canceling || sending}>
                {canceling ? 'Canceling…' : 'Cancel invitation'}
              </button>
            </div>
          </footer>
        </div>
      )}
    </Drawer>
  );
};

interface InviteMemberDrawerProps {
  open: boolean;
  saving: boolean;
  error: string | null;
  roles: Role[];
  onClose: () => void;
  onSubmit: (payload: InvitationCreate) => void;
}

const InviteMemberDrawer = ({ open, saving, error, roles, onClose, onSubmit }: InviteMemberDrawerProps) => {
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRoleIds([]);
      setExpiresInDays(14);
      setMessage('');
    }
  }, [open]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({ email, roleIds, expiresInDays, message: message.trim() ? message : undefined });
  };

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    setRoleIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, roleId]));
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  return (
    <Drawer open={open} title="Invite member" subtitle="Send a single invitation" onClose={onClose} width="wide">
      <form className="people-drawer" onSubmit={handleSubmit}>
        {error && <div className="people-alert people-alert--danger">{error}</div>}
        <section className="people-drawer__section">
          <label className="people-form__label">
            Email address
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={saving} />
          </label>
        </section>
        <section className="people-drawer__section">
          <header>
            <h3>Roles</h3>
            <p>Select the roles granted immediately when the invite is accepted.</p>
          </header>
          <RoleSelector roles={roles} selected={roleIds} onChange={setRoleIds} onToggle={handleRoleToggle} disabled={saving} />
        </section>
        <section className="people-drawer__section people-drawer__grid">
          <label className="people-form__label">
            Expires in (days)
            <input
              type="number"
              min={1}
              max={90}
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(Number(event.target.value) || 14)}
              disabled={saving}
            />
          </label>
          <label className="people-form__label">
            Message (optional)
            <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows={4} disabled={saving} />
          </label>
        </section>
        <footer className="people-drawer__footer">
          <div className="people-drawer__actions">
            <button type="button" className="ui-button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="ui-button ui-button--primary" disabled={saving || !email || roleIds.length === 0}>
              {saving ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </footer>
      </form>
    </Drawer>
  );
};

interface RoleDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  record: Role | null;
  etag: string | null;
  error: string | null;
  success: string | null;
  usage: number;
  onClose: () => void;
  onSave: (payload: { mode: 'create'; body: RoleCreate } | { mode: 'edit'; body: RoleUpdate }) => void;
  onDelete?: () => void;
  onViewMembers: (slug: string) => void;
}

interface PermissionDraft {
  id: string;
  resource: PermissionResource;
  verbs: Set<PermissionVerb>;
}

const RoleDrawer = ({
  open,
  mode,
  loading,
  saving,
  deleting,
  record,
  etag,
  error,
  success,
  usage = 0,
  onClose,
  onSave,
  onDelete,
  onViewMembers
}: RoleDrawerProps) => {
  const isEdit = mode === 'edit' && record;
  const isSystemRole = record?.scope === 'system';
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<RoleStatusValue>('active');
  const [permissionDrafts, setPermissionDrafts] = useState<PermissionDraft[]>([]);
  const selectablePermissionResources = useMemo(
    () => uniqueStringValues([...knownPermissionResources, ...permissionDrafts.map((draft) => normalizePermissionResource(draft.resource))]),
    [permissionDrafts]
  );

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (mode === 'create') {
      setSlug('');
      setName('');
      setDescription('');
      setStatus('active');
      if (knownPermissionResources.length > 0) {
        setPermissionDrafts([{ id: randomId(), resource: knownPermissionResources[0], verbs: new Set<PermissionVerb>(['read']) }]);
      } else {
        setPermissionDrafts([]);
      }
      return;
    }
    if (!record) {
      return;
    }
    setSlug(record.slug ?? '');
    setName(record.name ?? '');
    setDescription(record.description ?? '');
    setStatus(record.status ?? 'active');
    setPermissionDrafts(
      record.permissions.map((perm) => ({
        id: randomId(),
        resource: normalizePermissionResource(perm.resource),
        verbs: new Set<PermissionVerb>(normalizePermissionVerbs(perm.verbs))
      }))
    );
  }, [etag, mode, record]);

  const firstUnusedPermissionResource = (drafts: PermissionDraft[]): PermissionResource | undefined => {
    const usedResources = new Set(drafts.map((draft) => draft.resource));
    return selectablePermissionResources.find((resource) => !usedResources.has(resource));
  };

  const availableResourcesForDraft = (draftId: string): PermissionResource[] => {
    const current = permissionDrafts.find((draft) => draft.id === draftId);
    const selectedByOthers = new Set(
      permissionDrafts.filter((draft) => draft.id !== draftId).map((draft) => draft.resource)
    );
    return selectablePermissionResources.filter((resource) => resource === current?.resource || !selectedByOthers.has(resource));
  };

  const handlePermissionToggle = (id: string, verb: PermissionVerb, checked: boolean) => {
    setPermissionDrafts((prev) =>
      prev.map((draft) => {
        if (draft.id !== id) {
          return draft;
        }
        const verbs = new Set(draft.verbs);
        if (checked) {
          verbs.add(verb);
        } else {
          verbs.delete(verb);
        }
        return { ...draft, verbs };
      })
    );
  };

  const handlePermissionResourceChange = (id: string, resource: PermissionResource) => {
    setPermissionDrafts((prev) => {
      const nextDrafts = prev.map((draft) => (draft.id === id ? { ...draft, resource } : draft));
      if (mode !== 'create') {
        return nextDrafts;
      }

      const changedIndex = nextDrafts.findIndex((draft) => draft.id === id);
      const isLastRow = changedIndex === nextDrafts.length - 1;
      const unusedResource = firstUnusedPermissionResource(nextDrafts);
      if (isLastRow && unusedResource) {
        return [...nextDrafts, { id: randomId(), resource: unusedResource, verbs: new Set<PermissionVerb>(['read']) }];
      }
      return nextDrafts;
    });
  };

  const handlePermissionRemove = (id: string) => {
    setPermissionDrafts((prev) => prev.filter((draft) => draft.id !== id));
  };

  const handlePermissionAdd = () => {
    const unusedResource = firstUnusedPermissionResource(permissionDrafts);
    if (!unusedResource) {
      return;
    }
    setPermissionDrafts((prev) => [...prev, { id: randomId(), resource: unusedResource, verbs: new Set<PermissionVerb>(['read']) }]);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const permissions: Permissionschema[] = permissionDrafts.map((draft) => ({
      resource: normalizePermissionResource(draft.resource) as Permissionschema['resource'],
      verbs: normalizePermissionVerbs(Array.from(draft.verbs.values())) as Permissionschema['verbs']
    }));
    const nextCreateStatus: RoleStatusValue = status;
    const nextUpdateStatus: RoleStatusValue = status;

    if (mode === 'create') {
      onSave({
        mode: 'create',
        body: {
          slug: slug.trim(),
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          status: nextCreateStatus,
          permissions
        }
      });
    } else if (mode === 'edit') {
      onSave({
        mode: 'edit',
        body: {
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          status: nextUpdateStatus,
          permissions
        }
      });
    }
  };

  const canAddPermission = Boolean(firstUnusedPermissionResource(permissionDrafts));

  const drawerActions =
    mode === 'create'
      ? (
          <button
            type="button"
            className="drawer-icon-button"
            aria-label="Save role"
            title="Save role"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={saving || deleting}
          >
            <SaveIcon size={18} />
          </button>
        )
      : record
      ? (
          <button
            type="button"
            className="drawer-icon-button"
            aria-label="Save role"
            title="Save role"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={saving || deleting || isSystemRole}
          >
            <SaveIcon size={18} />
          </button>
        )
      : null;

  return (
    <Drawer
      open={open}
      title={mode === 'create' ? 'Create role' : record?.name ?? record?.slug ?? 'Role'}
      subtitle={mode === 'create' ? 'Define permissions for a new role' : record?.slug}
      onClose={onClose}
      width="wide"
      actions={drawerActions}
    >
      {loading && <p>Loading role…</p>}
      {!loading && mode === 'edit' && !record && <p className="people-empty">Unable to load role.</p>}
      {(!loading && mode === 'create') || (!loading && record) ? (
        <form className="people-drawer" onSubmit={handleSubmit} ref={formRef}>
          {error && <div className="people-alert people-alert--danger">{error}</div>}
          {success && <div className="people-alert people-alert--success">{success}</div>}
          {mode === 'edit' && record && (
            <div className="record-summary">
              <div className="record-summary__grid">
                <div>
                  <span>Slug</span>
                  <strong>{record.slug}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{record.status}</strong>
                </div>
                <div>
                  <span>Scope</span>
                  <strong>{record.scope}</strong>
                </div>
                <div>
                  <span>Members</span>
                  <strong>{formatNumber(usage)}</strong>
                </div>
              </div>
            </div>
          )}

          {mode === 'create' && (
            <section className="people-drawer__section">
              <label className="people-form__label">
                Slug
                <input type="text" value={slug} onChange={(event) => setSlug(event.target.value)} required disabled={saving || deleting} />
              </label>
            </section>
          )}

          <section className="people-drawer__section people-drawer__grid">
            <label className="people-form__label">
              Name
              <input type="text" value={name} onChange={(event) => setName(event.target.value)} disabled={saving || deleting || isSystemRole} />
            </label>
            <label className="people-form__label">
              Description
              <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} disabled={saving || deleting || isSystemRole} />
            </label>
          </section>

          <section className="people-drawer__section">
            <header>
              <h3>Status</h3>
            </header>
            <select value={status} onChange={(event) => setStatus(event.target.value as RoleStatusValue)} disabled={saving || deleting || isSystemRole}>
              {roleStatusOrder.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </section>

          <section className="people-drawer__section">
            <header>
              <h3>Permissions</h3>
            </header>
            {permissionDrafts.length === 0 && <p className="people-empty">No permissions selected yet.</p>}
            {permissionDrafts.map((draft) => (
              <div key={draft.id} className="people-permission-row">
                <select
                  value={draft.resource}
                  onChange={(event) => handlePermissionResourceChange(draft.id, event.target.value as PermissionResource)}
                  disabled={saving || deleting || isSystemRole}
                >
                  {availableResourcesForDraft(draft.id).map((resource) => (
                    <option key={resource} value={resource}>
                      {resource}
                    </option>
                  ))}
                </select>
                <div className="people-permission-verbs">
                  {roleVerbOrder.map((verb) => (
                    <label key={`${draft.id}-${verb}`}>
                      <input
                        type="checkbox"
                        checked={draft.verbs.has(verb)}
                        onChange={(event) => handlePermissionToggle(draft.id, verb, event.target.checked)}
                        disabled={saving || deleting || isSystemRole}
                      />
                      <span>{verb}</span>
                    </label>
                  ))}
                </div>
                <button type="button" className="people-labels__remove" onClick={() => handlePermissionRemove(draft.id)} disabled={saving || deleting || isSystemRole}>
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="ui-button ui-button--ghost"
              onClick={handlePermissionAdd}
              disabled={saving || deleting || isSystemRole || !canAddPermission}
            >
              Add permission
            </button>
          </section>

          <footer className="people-drawer__footer">
            {isEdit && !isSystemRole && onDelete && (
              <div className="people-drawer__danger">
                <button type="button" className="ui-button ui-button--danger" onClick={onDelete} disabled={saving || deleting}>
                  {deleting ? 'Deleting…' : 'Delete role'}
                </button>
              </div>
            )}
          </footer>
        </form>
      ) : null}
    </Drawer>
  );
};

interface RoleSelectorProps {
  roles: Role[];
  selected: string[];
  onChange: (next: string[]) => void;
  onToggle: (roleId: string, checked: boolean) => void;
  disabled?: boolean;
}

const RoleSelector = ({ roles, selected, onChange, onToggle, disabled }: RoleSelectorProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    onToggle(value, checked);
    if (checked) {
      onChange(Array.from(new Set([...selected, value])));
    } else {
      onChange(selected.filter((id) => id !== value));
    }
  };

  return (
    <div className="people-role-selector">
      {roles.map((role) => (
        <label key={role.id ?? role.slug} className="people-role-selector__item">
          <input type="checkbox" value={role.id ?? ''} checked={selected.includes(role.id ?? '')} onChange={handleChange} disabled={disabled || !role.id} />
          <div>
            <strong>{role.name ?? role.slug}</strong>
            <span>{role.slug}</span>
            <span className="people-role-selector__scope">{role.scope === 'system' ? 'System role' : 'Org role'}</span>
          </div>
        </label>
      ))}
      {roles.length === 0 && <p className="people-empty">No roles available yet.</p>}
    </div>
  );
};

interface ApiKeyStatusBadgeProps {
  status?: ApiKeyStatusValue;
}

const ApiKeyStatusBadge = ({ status }: ApiKeyStatusBadgeProps) => {
  if (!status) {
    return <span className="people-chip people-chip--empty">Unknown</span>;
  }
  const tone = status === ApiKeyStatusEnum.Active ? 'people-chip--success' : 'people-chip--danger';
  return <span className={`people-chip ${tone}`}>{status}</span>;
};

interface ApiKeyDrawerProps {
  open: boolean;
  loading: boolean;
  saving: boolean;
  revoking: boolean;
  record: ApiKey | null;
  etag: string | null;
  error: string | null;
  success: string | null;
  roles: Role[];
  onClose: () => void;
  onSubmit: (update: ApiKeyUpdate) => void;
  onRevoke: () => void;
}

const ApiKeyDrawer = ({
  open,
  loading,
  saving,
  revoking,
  record,
  etag,
  error,
  success,
  roles,
  onClose,
  onSubmit,
  onRevoke
}: ApiKeyDrawerProps) => {
  const [name, setName] = useState('');
  const [drawerRoleIds, setDrawerRoleIds] = useState<string[]>([]);
  const [expiresAtInput, setExpiresAtInput] = useState('');
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (!record) {
      setName('');
      setDrawerRoleIds([]);
      setExpiresAtInput('');
      return;
    }
    setName(record.name ?? '');
    setDrawerRoleIds(record.roleIds ?? []);
    setExpiresAtInput(formatDateTimeLocalInput(record.expiresAt ?? null));
  }, [record, etag]);

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    setDrawerRoleIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, roleId]));
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const trimmedName = name.trim();
  const initialName = record?.name ?? '';
  const rolesChanged = !haveSameElements(drawerRoleIds, record?.roleIds ?? []);
  const initialExpiry = formatDateTimeLocalInput(record?.expiresAt ?? null);
  const expiresChanged = expiresAtInput !== initialExpiry;
  const isRevoked = record?.status === ApiKeyStatusEnum.Revoked;

  const drawerActions = (
    <button
      type="button"
      className="drawer-icon-button"
      aria-label="Save API key"
      title="Save API key"
      onClick={() => formRef.current?.requestSubmit()}
      disabled={saving || revoking || isRevoked}
    >
      <SaveIcon size={18} />
    </button>
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!record) {
      return;
    }
    const update: ApiKeyUpdate = {};
    if (trimmedName && trimmedName !== initialName) {
      update.name = trimmedName;
    }
    if (rolesChanged) {
      update.roleIds = drawerRoleIds;
    }
    if (expiresChanged) {
      if (expiresAtInput) {
        update.expiresAt = new Date(expiresAtInput);
      }
    }
    if (Object.keys(update).length === 0) {
      return;
    }
    onSubmit(update);
  };

  return (
    <Drawer
      open={open}
      title={record?.name ?? 'API key'}
      subtitle={record?.prefix ? `${record.prefix}${record.last4 ?? ''}` : undefined}
      onClose={onClose}
      width="wide"
      actions={drawerActions}
    >
      {loading && <p>Loading API key…</p>}
      {!loading && !record && <p className="people-empty">Unable to load API key details.</p>}
      {!loading && record && (
        <form className="people-drawer" onSubmit={handleSubmit} ref={formRef}>
          {error && <div className="people-alert people-alert--danger">{error}</div>}
          {success && <div className="people-alert people-alert--success">{success}</div>}
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Name</span>
                <strong>{record.name ?? '—'}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{record.status ?? '—'}</strong>
              </div>
              <div>
                <span>Identifier</span>
                <strong>{record.prefix ? `${record.prefix}${record.last4 ?? ''}` : record.last4 ?? '—'}</strong>
              </div>
              <div>
                <span>Last used</span>
                <strong>{formatDateTime(record.lastUsedAt ?? null)}</strong>
              </div>
            </div>
          </div>

          <section className="people-drawer__section">
            <header>
              <h3>General</h3>
              <p>Update the friendly name or inspect metadata.</p>
            </header>
            <label className="people-form__label">
              Name
              <input type="text" value={name} onChange={(event) => setName(event.target.value)} disabled={saving || revoking || isRevoked} />
            </label>
            <div className="people-drawer__meta">
              <span>
                <strong>Status:</strong> <ApiKeyStatusBadge status={record.status as ApiKeyStatusValue} />
              </span>
              <span>
                <strong>Created:</strong> {formatDateTime(record.createdAt ?? null)}
              </span>
              <span>
                <strong>Updated:</strong> {formatDateTime(record.updatedAt ?? null)}
              </span>
              <span>
                <strong>Last used:</strong> {formatDateTime(record.lastUsedAt ?? null)}
              </span>
            </div>
          </section>

          <section className="people-drawer__section">
            <header>
              <h3>Roles</h3>
              <p>Assign roles to control the permissions granted to this API key.</p>
            </header>
            <RoleSelector
              roles={roles}
              selected={drawerRoleIds}
              onChange={setDrawerRoleIds}
              onToggle={handleRoleToggle}
              disabled={saving || revoking || isRevoked}
            />
          </section>

          <section className="people-drawer__section people-drawer__grid">
            <label className="people-form__label">
              Expires at
              <input
                type="datetime-local"
                value={expiresAtInput}
                onChange={(event) => setExpiresAtInput(event.target.value)}
                disabled={saving || revoking || isRevoked}
              />
            </label>
            <div className="people-drawer__meta">
              <strong>Identifier:</strong> {record.prefix ? `${record.prefix}${record.last4 ?? ''}` : record.last4 ?? '—'}
            </div>
          </section>

          <footer className="people-drawer__footer">
            <div className="people-drawer__danger">
              <button type="button" className="ui-button ui-button--danger" onClick={onRevoke} disabled={saving || revoking || isRevoked}>
                {revoking ? 'Revoking…' : 'Revoke key'}
              </button>
            </div>
          </footer>
        </form>
      )}
    </Drawer>
  );
};

interface CreateApiKeyDrawerProps {
  open: boolean;
  saving: boolean;
  error: string | null;
  response: ApiKeyCreateResponse | null;
  roles: Role[];
  onClose: () => void;
  onSubmit: (payload: ApiKeyCreate) => void;
}

const CreateApiKeyDrawer = ({ open, saving, error, response, roles, onClose, onSubmit }: CreateApiKeyDrawerProps) => {
  const [name, setName] = useState('');
  const [drawerRoleIds, setDrawerRoleIds] = useState<string[]>([]);
  const [expiresAtInput, setExpiresAtInput] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setDrawerRoleIds([]);
      setExpiresAtInput('');
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!response) {
      setCopied(false);
    }
  }, [response]);

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    setDrawerRoleIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, roleId]));
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload: ApiKeyCreate = {
      name: name.trim(),
      roleIds: drawerRoleIds
    };
    if (expiresAtInput) {
      payload.expiresAt = new Date(expiresAtInput);
    }
    onSubmit(payload);
  };

  const canSubmit = name.trim().length > 0 && drawerRoleIds.length > 0 && !saving && !response;

  const handleCopySecret = async () => {
    if (!response?.secret) {
      return;
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(response.secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy API key secret', err);
    }
  };

  return (
    <Drawer open={open} title="Create API key" onClose={onClose} width="wide">
      <form className="people-drawer" onSubmit={handleSubmit}>
        {error && <div className="people-alert people-alert--danger">{error}</div>}
        {response && (
          <div className="people-alert people-alert--success">
            <p>
              API key <strong>{response.name}</strong> created successfully. Store the secret securely—you will not be able to retrieve it again.
            </p>
            {response.secret && (
              <div className="people-secret">
                <code>{response.secret}</code>
                <button type="button" className="ui-button ui-button--ghost" onClick={handleCopySecret}>
                  {copied ? 'Copied!' : 'Copy secret'}
                </button>
              </div>
            )}
          </div>
        )}

        <section className="people-drawer__section">
          <label className="people-form__label">
            Name
            <input type="text" value={name} onChange={(event) => setName(event.target.value)} disabled={saving || Boolean(response)} required />
          </label>
        </section>

        <section className="people-drawer__section">
          <header>
            <h3>Roles</h3>
            <p>Select at least one role. The API key inherits the permissions granted to these roles.</p>
          </header>
          <RoleSelector
            roles={roles}
            selected={drawerRoleIds}
            onChange={setDrawerRoleIds}
            onToggle={handleRoleToggle}
            disabled={saving || Boolean(response) || roles.length === 0}
          />
        </section>

        <section className="people-drawer__section people-drawer__grid">
          <label className="people-form__label">
            Expires at (optional)
            <input
              type="datetime-local"
              value={expiresAtInput}
              onChange={(event) => setExpiresAtInput(event.target.value)}
              disabled={saving || Boolean(response)}
            />
          </label>
        </section>

        <footer className="people-drawer__footer">
          <div className="people-drawer__actions">
            <button type="button" className="ui-button" onClick={onClose} disabled={saving}>
              {response ? 'Close' : 'Cancel'}
            </button>
            <button type="submit" className="ui-button ui-button--primary" disabled={!canSubmit}>
              {saving ? 'Creating…' : response ? 'Done' : 'Create API key'}
            </button>
          </div>
        </footer>
      </form>
    </Drawer>
  );
};

export type { MemberFilters, InvitationFilters, RoleFilters, ApiKeyFilters };
